import axios from 'axios';
import type {
  HistoricalBar,
  Fundamentals,
  CupHandleOverlayResponse,
  FibGoldenZoneOverlayResponse,
  DarvasBoxOverlayResponse,
  StrongSupportOverlayResponse,
} from '../lib/deepAnalysis/types';
import type { EffectiveConfig, AuditEntry } from '../store/slices/runtimeConfigSlice';
import type { AdminUserActivityResponse } from '../types';
import {
  StockData,
  FilterConfig,
  StatusResponse,
  ScreeningStatus,
  ChartData,
  AIAnalysisData,
  ColorAnalysisData,
  ImageAnalysisData,
  ChartSnapshotResult,
  Favorite,
  BuySignalAnalysisResult,
  SingleBuySignalResult,
  User,
  RoleRequest,
  RoleManagementOptions,
  UserManagementFilters,
  RoleRequestFilters,
  AdminStats,
  GrokSentimentResponse,
  GrokBatchSentimentResponse,
  PerplexityStockAnalysisResponse,
  MATouchAlertRule,
  MATouchAlertRulePayload,
  MATouchAlertEvent,
  MATouchAlertStatus,
  BuyActionWatch,
  BuyActionWatchPayload,
  BuyActionEvent,
  TelegramAlertStatus,
  WhatsAppAlertStatus,
  MATouchAlertHealth,
  ReplayResult,
  AdminAlertsUserStatus,
  AdminAlertsLogResponse,
  AdminE2ETestResult,
  AdminDataStats,
  DatabaseTablesResponse,
  PaginatedTableData,
  AdminLogData,
  AdminActionResponse,
  DebugTickerResult,
  ScreeningStats,
  CurrentUser,
  UserConfig,
  AutoUpdateStatus,
  AgentsResponse,
  AgentResponse,
  AgentActionResponse,
  AgentRunResponse,
  AgentHistoryResponse,
  FlagAgent,
  FlagState,
  FlagAgentRunResult,
  FlagConditionMeta
} from '../types';
import { SEPAData, convertSEPAResponse, AIAnalysis, convertAIAnalysisResponse } from '../types/sepa';
import type {
  PositionFeatureStatus,
  PositionsListResponse,
  PositionDetailResponse,
  PositionAlertsResponse,
  PositionTimelineResponse,
  PositionStatsResponse,
  PositionDailySummaryResponse,
  CreatePositionPayload,
  UpdatePositionPayload,
  UpdatePositionLevelsPayload,
  ClosePositionPayload,
  PositionListFilters,
} from '../types/positions';
import { PatternDetectionData, convertPatternDetectionResult } from '../types/pattern';
import type { FinvizFilterValues, FinvizScreenerResult } from '../components/ScreeningFiltersDialog/types';
import { authService } from '../firebase';
import { resolveBullishMomentumEffective } from '../components/filterModeRules';

import { getApiBaseUrl } from './apiBaseUrl';

// Base URL is resolved once at load; for localhost we rely on runtime check above
const API_BASE_URL: string = getApiBaseUrl();

// Enhanced error types for better error handling
export interface ApiError extends Error {
  type: 'AbortError' | 'NetworkError' | 'ServerError' | 'ValidationError' | 'TimeoutError';
  status?: number;
  code?: string;
  details?: any;
}

// Request options interface
interface RequestOptions {
  retries?: number;
  retryDelay?: number;
  timeout?: number;
  cacheTTL?: number;
  debounce?: number;
  skipCache?: boolean;
  signal?: AbortSignal; // Add AbortSignal support
}

// Active request controllers for cancellation
const activeRequests = new Map<string, AbortController>();

// Module-level guard so the `token_revoked` 401 path (admin force-logout-all)
// does not re-enter `authService.signOut()` while a previous call is still
// in flight. A revoked session can produce a burst of parallel 401s from
// in-flight polls; we only want one sign-out per page load. Reset on
// signOut failure so a subsequent 401 can retry.
let _signingOut = false;
export function __resetSigningOutForTests(): void {
  _signingOut = false;
}

// Request cache with TTL
const requestCache = new Map<string, { data: any; timestamp: number; ttl: number }>();

// Request debounce map
const requestDebounceMap = new Map<string, ReturnType<typeof setTimeout>>();

// Helper function to create cache key
const createCacheKey = (method: string, url: string, data?: any) => {
  return `${method}:${url}:${JSON.stringify(data || {})}`;
};

// Helper function to get cached data
const getCachedData = (cacheKey: string) => {
  const cached = requestCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.data;
  }
  if (cached) {
    requestCache.delete(cacheKey);
  }
  return null;
};

// Helper function to cache data
const setCachedData = (cacheKey: string, data: any, ttl: number = 300000) => {
  requestCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
    ttl
  });
};

// Enhanced debounced request wrapper with cancellation support
const debounceRequest = <T>(key: string, fn: () => Promise<T>, delay: number = 300): Promise<T> => {
  return new Promise((resolve, reject) => {
    // Clear existing timeout and cancel any active request
    const existingTimeout = requestDebounceMap.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Cancel any existing request for this key
    const existingController = activeRequests.get(key);
    if (existingController) {
      existingController.abort();
      activeRequests.delete(key);
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        requestDebounceMap.delete(key);
      }
    }, delay);

    requestDebounceMap.set(key, timeout);
  });
};

// Helper function to create enhanced API errors
const createApiError = (error: any, type: ApiError['type']): ApiError => {
  const apiError = new Error(error.message || 'Unknown error') as ApiError;
  apiError.type = type;
  apiError.status = error.response?.status;
  apiError.code = error.code;
  apiError.details = error.response?.data;
  return apiError;
};

// Helper function to determine error type
const getErrorType = (error: any): ApiError['type'] => {
  if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
    return 'AbortError';
  }
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return 'TimeoutError';
  }
  if (!error.response) {
    return 'NetworkError';
  }
  if (error.response.status >= 400 && error.response.status < 500) {
    return 'ValidationError';
  }
  return 'ServerError';
};

// Environment configuration for debugging
const isDebugMode = process.env.REACT_APP_API_DEBUG === 'true';
const isDevelopment = process.env.NODE_ENV === 'development';

// Enhanced axios instance with better configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased timeout for long operations
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable request/response logging only when explicitly enabled
  ...(isDevelopment && isDebugMode && {
    transformRequest: [(data) => {
      console.log('🚀 API Request:', data);
      return JSON.stringify(data);
    }],
    transformResponse: [(data) => {
      try {
        const parsed = JSON.parse(data);
        console.log('📡 API Response:', parsed);
        return parsed;
      } catch {
        return data;
      }
    }],
  }),
});

// Request interceptor for adding auth tokens or other headers
api.interceptors.request.use(
  async (config) => {
    // Add timestamp to prevent caching - but only if the URL doesn't already
    // carry one. BUG-8 fix 2026-05-08: callers that hand-build `?_t=…` (e.g.
    // /api/filters/database, /perplexity/*) used to produce duplicate
    // `_t=&_t=` query strings because the interceptor unconditionally
    // appended its own.
    const urlHasCacheBuster =
      typeof config.url === 'string' && config.url.includes('_t=');
    const paramsHasCacheBuster =
      config.params && Object.prototype.hasOwnProperty.call(config.params, '_t');
    if (!urlHasCacheBuster && !paramsHasCacheBuster) {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    // Add Firebase ID token if available (non-blocking)
    try {
      if (authService.isAvailable()) {
        const idToken = await authService.getIdToken();
        if (idToken) {
          config.headers.Authorization = `Bearer ${idToken}`;
        }
      }
    } catch (error) {
      // Non-blocking: log error but continue with request
      console.warn('⚠️ Failed to add auth token to request:', error);
    }

    return config;
  },
  (error) => {
    // axios invokes this callback only when a *request* interceptor itself
    // throws or rejects (e.g. network down before send). HTTP responses,
    // including 401s, never land here - they go to the response interceptor.
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Request cancellation is expected during rapid UI interactions/polling.
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return Promise.reject(error);
    }

    const originalRequest = error?.config;

    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const body = (error.response?.data ?? {}) as { error?: string };

      // Force-logout-all: admin set MIN_TOKEN_IAT_EPOCH and backend rejects
      // every ID token with iat < epoch. Refreshing the ID token would
      // produce a token with the same iat (until the user re-authenticates),
      // so don't retry - sign out instead. `_signingOut` collapses a burst of
      // parallel 401s into a single sign-out per page load; on signOut
      // failure we clear the flag so the next 401 can try again.
      if (body.error === 'token_revoked') {
        if (!_signingOut) {
          _signingOut = true;
          try {
            await authService.signOut();
          } catch (signOutErr) {
            _signingOut = false;
            console.warn('⚠️ Failed to sign out after token_revoked:', signOutErr);
          }
        }
        return Promise.reject(error);
      }

      // Generic 401 (e.g. stale ID token): refresh once and replay the
      // request. `_retry` flag on the original config prevents an infinite
      // loop if the refreshed token also returns 401.
      originalRequest._retry = true;
      try {
        if (authService.isAvailable()) {
          const idToken = await authService.getIdToken(true);
          if (idToken) {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${idToken}`;
            console.log('🔄 Retrying request with refreshed token');
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.warn('⚠️ Failed to refresh auth token:', refreshError);
      }
    }

    // Log errors for debugging - show full details including backend error message.
    // 409 on /screen_stocks is an expected concurrency signal, so keep it as info-level.
    const backendError = error.response?.data?.error || error.response?.data?.message;
    const logPayload = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      backendError: backendError || error.response?.data,
      frontendMessage: error.message,
    };
    const isExpectedScreeningConflict = error.response?.status === 409 && `${error.config?.url || ''}`.includes('/screen_stocks');
    // BUG-12 fix 2026-05-08: render URL + status in the message so the
    // log line is actually triagable in shipped logs (Chrome console
    // collapses object args to "Object" - operations couldn't grep).
    const summary = `${logPayload.method?.toUpperCase() || 'GET'} ${logPayload.url || '(no url)'} → ${logPayload.status ?? 'no-response'}`;
    if (isExpectedScreeningConflict) {
      console.info(`API Conflict (expected): ${summary}`, logPayload);
    } else {
      console.error(`API Error: ${summary}`, logPayload);
    }
    return Promise.reject(error);
  }
);

/**
 * Enhanced API request function with retry logic and better error handling
 */
export async function makeRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: any,
  options?: RequestOptions
): Promise<T> {
  const { retries = 2, retryDelay = 1000, timeout, cacheTTL = 300000, debounce = 0, skipCache = false, signal } = options || {};

  // Create cache key for caching
  const cacheKey = createCacheKey(method, url, data);

  // Check cache first for GET requests
  if (method === 'GET' && !skipCache) {
    const cached = getCachedData(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const attemptRequest = async (attempt: number): Promise<T> => {
    // Create or use provided AbortController
    const controller = signal ? undefined : new AbortController();
    const requestSignal = signal || controller?.signal;

    // Store controller for potential cancellation
    if (controller) {
      activeRequests.set(cacheKey, controller);
    }

    try {
      const axiosConfig: any = {
        method,
        url,
        headers: {
          'Content-Type': 'application/json',
        },
        signal: requestSignal,
        ...(timeout && { timeout }),
      };

      if (data) {
        axiosConfig.data = data;
      }

      const response = await api(axiosConfig);
      const responseData = response.data;

      // Cache GET responses
      if (method === 'GET' && !skipCache) {
        setCachedData(cacheKey, responseData, cacheTTL);
      }

      return responseData;

    } catch (error: any) {
      // Don't retry if request was cancelled
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw createApiError(error, 'AbortError');
      }

      // Retry logic for network errors or 5xx errors
      const shouldRetry = attempt < retries && (
        !error.response ||
        (error.response.status >= 500 && error.response.status <= 599) ||
        error.code === 'ECONNABORTED' // Timeout
      );

      if (shouldRetry) {
        if (isDevelopment && isDebugMode) {
          console.warn(`Request failed (attempt ${attempt + 1}/${retries + 1}), retrying in ${retryDelay}ms...`);
        }
        await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt))); // Exponential backoff
        return attemptRequest(attempt + 1);
      }

      if (isDevelopment && isDebugMode) {
        console.error('API request failed:', {
          url,
          method,
          attempt: attempt + 1,
          error: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
      }

      // Create and throw enhanced API error
      const errorType = getErrorType(error);
      const apiError = createApiError(error, errorType);

      // Enhanced error handling with user-friendly messages
      if (error.response) {
        const status = error.response.status;
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message;

        switch (status) {
          case 400:
            apiError.message = `Bad request: ${message}`;
            break;
          case 401:
            apiError.message = 'Authentication required. Please check your credentials.';
            break;
          case 403: {
            const errorCode = error.response?.data?.error;
            if (errorCode === 'daily_limit_reached') {
              apiError.message = error.response?.data?.message || 'Daily scan limit reached. Upgrade to Pro for unlimited scans.';
            } else if (errorCode === 'Insufficient permissions') {
              apiError.message = error.response?.data?.message || 'Insufficient permissions for this action.';
            } else {
              apiError.message = message || 'Access denied. You don\'t have permission to perform this action.';
            }
            break;
          }
          case 404:
            apiError.message =
              'API returned 404. If the server responds but routes fail, another app may be on this port ' +
              'or REACT_APP_API_URL does not match the backend (see STOCK_SCREENER_PORT / start-backend.bat).';
            break;
          case 408:
            apiError.message = 'Request timeout. Please try again.';
            break;
          case 415:
            apiError.message = 'Invalid request format. Please try again.';
            break;
          case 422:
            apiError.message = `Validation error: ${message}`;
            break;
          case 409:
            apiError.message = message || 'Screening already in progress. Please wait for it to finish or stop it first.';
            break;
          case 429:
            if (data?.provider) {
              apiError.message = `Daily ${data.provider} AI quota exceeded (${data.used}/${data.limit}). Resets at midnight UTC.`;
            } else {
              apiError.message = 'Too many requests. Please wait a moment and try again.';
            }
            break;
          case 500:
            apiError.message = `Server error: ${message}`;
            break;
          case 502:
            apiError.message = 'Bad gateway. The server is temporarily unavailable.';
            break;
          case 503:
            apiError.message = 'Service temporarily unavailable. Please try again later.';
            break;
          case 504:
            apiError.message = 'Gateway timeout. The request took too long to process.';
            break;
          default:
            apiError.message = `Request failed (${status}): ${message}`;
        }
      } else if (error.request) {
        if (error.code === 'ECONNABORTED') {
          apiError.message = 'Request timeout. Please check your connection and try again.';
        } else {
          apiError.message = 'Cannot connect to server. Please check if the backend is running and your internet connection.';
        }
      } else {
        apiError.message = `Request error: ${error.message}`;
      }

      throw apiError;
    } finally {
      // Clean up active request tracking
      if (controller) {
        activeRequests.delete(cacheKey);
      }
    }
  };

  // Apply debouncing if specified
  if (debounce > 0) {
    return debounceRequest(cacheKey, () => attemptRequest(0), debounce);
  }

  return attemptRequest(0);
}

// Utility functions for request cancellation
export const cancelRequest = (cacheKey: string): void => {
  const controller = activeRequests.get(cacheKey);
  if (controller) {
    controller.abort();
    activeRequests.delete(cacheKey);
  }

  // Also clear any pending debounced requests
  const timeout = requestDebounceMap.get(cacheKey);
  if (timeout) {
    clearTimeout(timeout);
    requestDebounceMap.delete(cacheKey);
  }
};

export const cancelAllRequests = (): void => {
  // Cancel all active requests
  activeRequests.forEach((controller) => {
    controller.abort();
  });
  activeRequests.clear();

  // Clear all pending debounced requests
  requestDebounceMap.forEach((timeout) => {
    clearTimeout(timeout);
  });
  requestDebounceMap.clear();
};

// Helper to create cache key for external use
export const createRequestCacheKey = createCacheKey;

export const invalidateResultsCache = (): void => {
  requestCache.delete(createCacheKey('GET', '/results'));
};

// Helper to check if a request is active
export const isRequestActive = (cacheKey: string): boolean => {
  return activeRequests.has(cacheKey);
};

// Database stats
export const getDatabaseStats = async (): Promise<StatusResponse> => {
  try {
    const response = await api.get('/status');
    // Axios-like response: { data: StatusResponse }
    const data = response?.data ?? response;
    if (data && (data as any).database_stats) {
      return data as StatusResponse;
    }
  } catch (error) {
    // Fall through to a safe default below.
  }

  // Safe fallback for test environments where `/status` isn't mocked.
  return {
    screening: {
      status: 'idle',
      current_stock: '',
      processed_count: 0,
      total_stocks: 0,
      passing_count: 0,
      failed_count: 0,
      error_count: 0,
      message: '',
      results: [],
      last_updated: null,
    },
    data_update: {
      status: 'idle',
      current_stock: '',
      processed_count: 0,
      total_stocks: 0,
      successful_count: 0,
      failed_count: 0,
      message: '',
      errors: [],
      completion_summary: '',
      last_completed: null,
      show_notification: false,
    },
    database_stats: {
      total_stocks: 0,
      stocks_with_data: 0,
      stocks_with_targets: 0,
      latest_passing: 0,
    },
  } as StatusResponse;
};

// Filters
export const getFilters = async (): Promise<FilterConfig> => {
  return makeRequest<FilterConfig>('GET', '/filters');
};

export interface PresetBundle {
  id: string;
  name: string;
  description: string;
}

export interface PresetBundlesResponse {
  bundles: PresetBundle[];
  active: string | null;
}

export interface ApplyBundleResponse {
  applied: string;
  active: string | null;
  filter_config?: Record<string, unknown>;
}

export const getFilterBundles = async (): Promise<PresetBundlesResponse> => {
  return makeRequest<PresetBundlesResponse>('GET', '/api/filters/bundles');
};

export const applyFilterBundle = async (bundleId: string): Promise<ApplyBundleResponse> => {
  return makeRequest<ApplyBundleResponse>(
    'POST',
    `/api/filters/bundles/${encodeURIComponent(bundleId)}/apply`
  );
};

export const updateFilters = async (filters: FilterConfig): Promise<void> => {
  try {
    const bullishMomentumEnabled = resolveBullishMomentumEffective(filters);

    // Convert frontend filter structure to backend-compatible format
    const backendFilters = {
      min_market_cap: filters.min_market_cap,
      min_price_target_percent: filters.min_price_target_percent,
      ma_proximity_min: filters.ma_proximity_min,
      ma_proximity_max: filters.ma_proximity_max,
      // Recent MA150 cross gate (default-on). Defensive defaults so the
      // backend never receives `undefined` if Redux was partially hydrated.
      require_recent_ma150_cross:
        (filters as any).require_recent_ma150_cross ?? true,
      recent_ma150_cross_lookback_days:
        (filters as any).recent_ma150_cross_lookback_days ?? 6,
      recent_ma150_cross_bypass_pct:
        (filters as any).recent_ma150_cross_bypass_pct ?? 0.02,
      ma_filter_mode: filters.ma_filter_mode,
      sh_avgvol: (filters as any).sh_avgvol ?? '',
      sh_relvol: (filters as any).sh_relvol ?? '',
      sh_curvol: (filters as any).sh_curvol ?? '',
      trend_period_months: filters.trend_period_months,
      standard_screening_enabled: filters.standard_screening_enabled,
      bullish_momentum_enabled: bullishMomentumEnabled,
      // NEW: Enhanced trend configuration
      trend_direction: filters.trend_direction,
      trend_strength: filters.trend_strength,
      momentum_confirmation: filters.momentum_confirmation,
      // Transition filters
      bullish_transition_enabled: filters.bullish_transition_enabled,
      bearish_transition_enabled: filters.bearish_transition_enabled,
      // Transition config (convert flat fields to nested)
      transition_config: {
        lookback_days: filters.transition_lookback_days || 60,
        require_momentum_confirm: filters.transition_require_momentum !== false,
        volume_confirmation: filters.transition_require_volume || false,
      },
      doji_filter_enabled: filters.doji_filter_enabled,
      cup_handle_filter_enabled: filters.cup_handle_filter_enabled,
      fib_golden_zone_filter_enabled: filters.fib_golden_zone_filter_enabled,
      fib_golden_zone_config: filters.fib_golden_zone_config,
      volume_structure_enabled: filters.volume_structure_enabled,
      darvas_box_filter_enabled: filters.darvas_box_filter_enabled,
      darvas_box_config: filters.darvas_box_config,
      head_shoulders_filter_enabled: filters.head_shoulders_filter_enabled,
      double_pattern_filter_enabled: filters.double_pattern_filter_enabled,
      breakout_filter_enabled: filters.breakout_filter_enabled,
      green_doji_filter_enabled: filters.green_doji_filter_enabled,
      strong_red_reversal_filter_enabled: filters.strong_red_reversal_filter_enabled,
      pullback_to_support_filter_enabled: filters.pullback_to_support_filter_enabled,
      active_category: filters.active_category,
      active_preset_bundle: (filters as any).active_preset_bundle ?? null,
      // MA Filter Selection (checkbox-based, replaces ma_filter_mode)
      ma_filter_selection: filters.ma_filter_selection,
      // MA Bounce Reversal Filter
      ma_bounce_reversal_enabled: filters.ma_bounce_reversal_enabled,
      ma_bounce_reversal_strategy: filters.ma_bounce_reversal_strategy,
      ma_bounce_reversal_config: filters.ma_bounce_reversal_config,
      // Instrument Type Filter
      instrument_type_filter: filters.instrument_type_filter,
      // MA150 V-Reversal Filter
      ma150_v_reversal_enabled: filters.ma150_v_reversal_enabled,
      ma150_v_reversal_strategy: filters.ma150_v_reversal_strategy,
      ma150_v_reversal_config: filters.ma150_v_reversal_config,
    };
    await makeRequest<void>('POST', '/filters', backendFilters);
  } catch (error: any) {
    throw new Error(`Failed to update filters: ${error.message}`);
  }
};

// Database-backed filter configuration (single source of truth)

// Helper to normalize filter config keys (handle camelCase <-> snake_case)
// Backend uses snake_case, frontend uses camelCase - normalize both ways
const normalizeFilterConfigKeys = (config: any, toSnakeCase: boolean): any => {
  if (!config) return config;

  const keyMappings: Record<string, string> = {
    // camelCase -> snake_case mappings
    goldenCrossFilterEnabled: 'golden_cross_filter_enabled',
    breakoutFilterEnabled: 'breakout_filter_enabled',
    cupHandleFilterEnabled: 'cup_handle_filter_enabled',
    headShouldersFilterEnabled: 'head_shoulders_filter_enabled',
    doublePatternFilterEnabled: 'double_pattern_filter_enabled',
    strongRedReversalFilterEnabled: 'strong_red_reversal_filter_enabled',
    greenDojiFilterEnabled: 'green_doji_filter_enabled',
    pullbackToSupportFilterEnabled: 'pullback_to_support_filter_enabled',
    shAvgvol: 'sh_avgvol',
    shRelvol: 'sh_relvol',
    shCurvol: 'sh_curvol',
    dojiFilterEnabled: 'doji_filter_enabled',
    activeCategory: 'active_category',
    minMarketCap: 'min_market_cap',
    maProximityMin: 'ma_proximity_min',
    maProximityMax: 'ma_proximity_max',
    requireRecentMa150Cross: 'require_recent_ma150_cross',
    recentMa150CrossLookbackDays: 'recent_ma150_cross_lookback_days',
    recentMa150CrossBypassPct: 'recent_ma150_cross_bypass_pct',
    minPriceTargetPercent: 'min_price_target_percent',
    trendPeriodMonths: 'trend_period_months',
    priceTargetMode: 'price_target_mode',
    maFilterSelection: 'ma_filter_selection',
    maTimeframeMode: 'ma_timeframe_mode',
    rsiRecoveryEnabled: 'rsi_recovery_enabled',
    rsiRecoveryStrategy: 'rsi_recovery_strategy',
    rsiRecoveryConfig: 'rsi_recovery_config',
    maBounceReversalEnabled: 'ma_bounce_reversal_enabled',
    maBounceReversalStrategy: 'ma_bounce_reversal_strategy',
    maBounceReversalConfig: 'ma_bounce_reversal_config',
    ma150VReversalEnabled: 'ma150_v_reversal_enabled',
    ma150VReversalStrategy: 'ma150_v_reversal_strategy',
    ma150VReversalConfig: 'ma150_v_reversal_config',
    supportRetestEnabled: 'support_retest_enabled',
    supportRetestStrategy: 'support_retest_strategy',
    supportRetestConfig: 'support_retest_config',
    standardScreeningEnabled: 'standard_screening_enabled',
    bullishMomentumEnabled: 'bullish_momentum_enabled',
    volumeSpikeEnabled: 'volume_spike_enabled',
    volumeSpikeConfig: 'volume_spike_config',
    priceActionEnabled: 'price_action_enabled',
    priceActionConfig: 'price_action_config',
    breakoutEnabled: 'breakout_enabled',
    breakoutExtendedEnabled: 'breakout_extended_enabled',  // BUG-CP-003
    breakoutConfig: 'breakout_config',
    fibGoldenZoneFilterEnabled: 'fib_golden_zone_filter_enabled',
    fibGoldenZoneConfig: 'fib_golden_zone_config',
    darvasBoxFilterEnabled: 'darvas_box_filter_enabled',
    darvasBoxConfig: 'darvas_box_config',
    volumeStructureEnabled: 'volume_structure_enabled',
    // NEW: Enhanced trend configuration
    trendDirection: 'trend_direction',
    trendStrength: 'trend_strength',
    momentumConfirmation: 'momentum_confirmation',
    // Transition filters
    bullishTransitionEnabled: 'bullish_transition_enabled',
    bearishTransitionEnabled: 'bearish_transition_enabled',
    // Transition config (flat fields)
    transitionLookbackDays: 'transition_lookback_days',
    transitionRequireMomentum: 'transition_require_momentum',
    transitionRequireVolume: 'transition_require_volume',
    // Transition config (nested)
    transitionConfig: 'transition_config',
    // Bearish transition config (flat fields)
    transitionLookbackDaysBearish: 'transition_lookback_days_bearish',
    transitionRequireMomentumBearish: 'transition_require_momentum_bearish',
    transitionRequireVolumeBearish: 'transition_require_volume_bearish',
    // Instrument type filtering
    instrumentTypeFilter: 'instrument_type_filter',
    // Index-based filtering
    indexFilters: 'index_filters',
  };

  const result: any = {};
  for (const [key, value] of Object.entries(config)) {
    const mappedKey = toSnakeCase ? keyMappings[key] : undefined;
    if (toSnakeCase && mappedKey) {
      result[mappedKey] = value;
    } else if (!toSnakeCase) {
      // Find the camelCase version
      const snakeKey = Object.entries(keyMappings).find(([, v]) => v === key)?.[0];
      result[snakeKey || key] = value;
    } else {
      result[key] = value;
    }
  }
  return result;
};

export const getDatabaseFilters = async (): Promise<FilterConfig | null> => {
  // BUG-CP-PERSIST FIX: Add cache-busting to ensure we always get fresh data from DB
  const response = await makeRequest<{ success: boolean; config: FilterConfig | null }>(
    'GET',
    '/api/filters/database?_t=' + Date.now()
  );

  // Handle case where config is null (first-time user, no saved filters)
  if (!response || response.config === null) {
    console.log('No stored filter config - frontend will use local defaults');
    return null;
  }

  // Handle case where config might be undefined
  if (!response || !response.config) {
    console.error('Invalid response from /api/filters/database:', response);
    throw new Error('Invalid filter configuration response from server');
  }

  // Normalize keys from snake_case (backend) to camelCase for consumers that need it
  const normalizedConfig = normalizeFilterConfigKeys(response.config, false);

  // CRITICAL FIX: Return DB values exactly as stored - NO default overrides.
  // First-time bootstrap should handle defaults explicitly, not on every load.
  // This ensures DB is the source of truth and user settings persist correctly.
  return {
    ...response.config,
    ...normalizedConfig,
  };
};



export const updateDatabaseFilters = async (filters: FilterConfig): Promise<void> => {
  try {
    // Normalize keys to snake_case for backend compatibility
    const normalizedFilters = normalizeFilterConfigKeys(filters, true);

    // Send entire filter config to database endpoint
    const response = await makeRequest<{success: boolean; message: string}>('POST', '/api/filters/database', normalizedFilters);

    // Validate that the save was actually successful
    if (!response.success) {
      throw new Error(response.message || 'Failed to save filters to database');
    }
  } catch (error: any) {
    // Provide more helpful error messages based on status code
    if (error.response?.status === 401) {
      throw new Error('Authentication required to save filters. Please log in to save your filter configuration.');
    }
    // Use server error message if available, otherwise fall back to generic message
    const serverError = error.response?.data?.error || error.response?.data?.message;
    throw new Error(serverError || `Failed to update database filters: ${error.message}`);
  }
};

export const syncFiltersFromDatabase = async (): Promise<FilterConfig> => {
  try {
    const response = await makeRequest<{success: boolean; message: string; config: FilterConfig}>('POST', '/api/filters/database/sync');
    return response.config;
  } catch (error: any) {
    throw new Error(`Failed to sync filters from database: ${error.message}`);
  }
};


// Screening with longer timeout
export const screenStocks = async (options?: {
  filter_category?: string;
  min_score?: number;
  include_entry_analysis?: boolean;
  sh_avgvol?: string;
  sh_relvol?: string;
  sh_curvol?: string;
  use_finviz_cache?: boolean;
  finviz_tickers?: string[];
}): Promise<void> => {
  try {
    const requestData = options || {};
    await makeRequest<void>('POST', '/screen_stocks', requestData, {
      timeout: 120000, // 2 minutes for screening operations
      retries: 0, // No retries - screening is a one-shot operation
    });
  } catch (error: any) {
    // Preserve the specific error message (e.g. 409 "Screening already in progress")
    throw new Error(error.message || 'Failed to start stock screening');
  }
};

export const stopScreening = async (): Promise<{ success: boolean; message: string }> => {
  try {
    return await makeRequest<{ success: boolean; message: string }>('POST', '/stop_screening', undefined, {
      timeout: 5000, // 5 seconds timeout
      retries: 0, // No retries for stop requests
    });
  } catch (error: any) {
    throw new Error(`Failed to stop screening: ${error.message}`);
  }
};

export const getResults = async (skipCache: boolean = false): Promise<StockData[]> => {
  return makeRequest<StockData[]>('GET', '/results', undefined, {
    cacheTTL: 30000, // 30 second cache (was 3 minutes)
    debounce: 500,
    skipCache: skipCache
  });
};

/** `grid`: request minimal payload (MA 150/200 + OHLC slice) for charts grid - faster load */
export const getChartData = async (
  ticker: string,
  timeframe: string = 'DAILY',
  signal?: AbortSignal,
  opts?: { grid?: boolean }
): Promise<ChartData> => {
  // Grid: ~1 trading year (252 sessions) for MA150/200 context; backend allows up to 365.
  const gridMode = opts?.grid ? '&mode=grid&days=252' : '';
  return makeRequest<ChartData>('GET', `/chart_data/${ticker}?timeframe=${timeframe}${gridMode}`, undefined, {
    // Grid mode: skip api-level cache - ChartsGridLightweightCell has its own LRU cache
    // to avoid double-caching the same payload in memory.
    cacheTTL: opts?.grid ? 0 : 300000,
    debounce: 500,
    ...(signal && { signal })
  });
};

export interface StockTickersResponse {
  tickers: string[];
  total_in_db: number;
  returned: number;
  error?: string;
}

/** List tickers stored in the database (autocomplete for chart symbol picker). */
export const getStockTickers = async (
  opts?: { search?: string; limit?: number; signal?: AbortSignal }
): Promise<StockTickersResponse> => {
  const sp = new URLSearchParams();
  const q = (opts?.search ?? '').trim();
  if (q) sp.set('search', q);
  const limit = opts?.limit ?? 400;
  sp.set('limit', String(Math.min(5000, Math.max(1, limit))));
  const qs = sp.toString();
  return makeRequest<StockTickersResponse>('GET', `/api/stocks/tickers${qs ? `?${qs}` : ''}`, undefined, {
    cacheTTL: 30_000,
    debounce: 0,
    retries: 1,
    ...(opts?.signal ? { signal: opts.signal } : {}),
  });
};

/** Intraday Yahoo preview (optional / legacy); charts grid uses daily `getChartData` with `grid: true` (~1Y). */
export const getChartPreviewIntraday = async (
  ticker: string,
  signal?: AbortSignal,
  opts?: { bars?: number; period?: string; interval?: string }
): Promise<ChartData> => {
  const enc = encodeURIComponent(ticker);
  const bars = opts?.bars ?? 120;
  const period = opts?.period ?? '5d';
  const interval = opts?.interval ?? '5m';
  const q = `period=${encodeURIComponent(period)}&interval=${encodeURIComponent(interval)}&bars=${bars}`;
  return makeRequest<ChartData>('GET', `/api/chart_preview/${enc}?${q}`, undefined, {
    cacheTTL: 0,
    debounce: 0,
    retries: 0,
    ...(signal && { signal }),
  });
};

// Refresh chart data (bypasses cache)
export const refreshChartData = async (ticker: string, timeframe: string = 'DAILY'): Promise<ChartData> => {
  return makeRequest<ChartData>('GET', `/chart_data/${ticker}?timeframe=${timeframe}`, undefined, {
    skipCache: true, // Force fresh data
    debounce: 500
  });
};

/**
 * Cup-and-Handle chart overlay (spec §12 Track B / §18).
 *
 * Backend computes ALL geometry - this just transports it. Returns safe,
 * non-null defaults on any error so callers can render a structured fallback
 * note instead of crashing (spec §18: never silently draw nothing). Required
 * string fields are always present (`status`, `patternQuality`, `reason`,
 * `schemaVersion`); `overlay` is the only nullable field.
 */
const EMPTY_CUP_HANDLE_OVERLAY: CupHandleOverlayResponse = {
  detected: false,
  status: 'none',
  patternQuality: 'none',
  confidence: 0,
  rejectReasons: [],
  reason: '',
  schemaVersion: 'cup_handle_overlay_v2',
  overlay: null,
};

export const getCupHandleOverlay = async (
  symbol: string,
  timeframe: 'DAILY' | 'WEEKLY' = 'DAILY',
  signal?: AbortSignal
): Promise<CupHandleOverlayResponse> => {
  if (!symbol) return { ...EMPTY_CUP_HANDLE_OVERLAY };
  try {
    const enc = encodeURIComponent(symbol.toUpperCase());
    const tf = timeframe === 'WEEKLY' ? 'WEEKLY' : 'DAILY';
    const raw = await makeRequest<Partial<CupHandleOverlayResponse>>(
      'GET',
      `/api/deep-analysis/cup-handle?symbol=${enc}&timeframe=${tf}`,
      undefined,
      {
        cacheTTL: 60_000,
        debounce: 250,
        retries: 1,
        ...(signal ? { signal } : {}),
      }
    );
    // Coerce to a fully-populated response with safe defaults so downstream
    // never sees a null required-string field (response-contract rule).
    return {
      detected: raw?.detected === true,
      status: raw?.status ?? 'none',
      patternQuality: raw?.patternQuality ?? 'none',
      confidence: Number.isFinite(raw?.confidence as number) ? (raw!.confidence as number) : 0,
      rejectReasons: Array.isArray(raw?.rejectReasons) ? raw!.rejectReasons! : [],
      reason: typeof raw?.reason === 'string' ? raw!.reason! : '',
      schemaVersion: 'cup_handle_overlay_v2',
      overlay: raw?.overlay ?? null,
    };
  } catch (e) {
    if (axios.isCancel?.(e)) throw e;
    console.warn('[api] getCupHandleOverlay failed', e);
    return { ...EMPTY_CUP_HANDLE_OVERLAY, reason: 'fetch_failed' };
  }
};

/**
 * Empty-overlay fallbacks for the fib / darvas / strong-support routes. Mirror
 * the cup-handle contract: required string fields are always present and
 * `overlay` is the only nullable field, so downstream never sees a null
 * required-string field (response-contract rule).
 */
const EMPTY_FIB_GOLDEN_ZONE_OVERLAY: FibGoldenZoneOverlayResponse = {
  detected: false,
  status: 'none',
  overlay: null,
  rejectReasons: [],
  reason: '',
  confidence: 0,
  schemaVersion: 0,
};

const EMPTY_DARVAS_BOX_OVERLAY: DarvasBoxOverlayResponse = {
  detected: false,
  status: 'none',
  patternQuality: 'none',
  overlay: null,
  rejectReasons: [],
  reason: '',
  confidence: 0,
  schemaVersion: 0,
};

const EMPTY_STRONG_SUPPORT_OVERLAY: StrongSupportOverlayResponse = {
  detected: false,
  status: 'none',
  overlay: null,
  rejectReasons: [],
  reason: '',
  confidence: 0,
  schemaVersion: 0,
};

export const getFibGoldenZoneOverlay = async (
  symbol: string,
  timeframe: 'DAILY' | 'WEEKLY' = 'DAILY',
  signal?: AbortSignal
): Promise<FibGoldenZoneOverlayResponse> => {
  if (!symbol) return { ...EMPTY_FIB_GOLDEN_ZONE_OVERLAY };
  try {
    const enc = encodeURIComponent(symbol.toUpperCase());
    const tf = timeframe === 'WEEKLY' ? 'WEEKLY' : 'DAILY';
    // Backend emits `time` as UNIX seconds already - pass through verbatim.
    const raw = await makeRequest<Partial<FibGoldenZoneOverlayResponse>>(
      'GET',
      `/api/deep-analysis/fib-golden-zone?symbol=${enc}&timeframe=${tf}`,
      undefined,
      {
        cacheTTL: 60_000,
        debounce: 250,
        retries: 1,
        ...(signal ? { signal } : {}),
      }
    );
    return {
      detected: raw?.detected === true,
      status: raw?.status ?? 'none',
      overlay: raw?.overlay ?? null,
      rejectReasons: Array.isArray(raw?.rejectReasons) ? raw!.rejectReasons! : [],
      reason: typeof raw?.reason === 'string' ? raw!.reason! : '',
      confidence: Number.isFinite(raw?.confidence as number) ? (raw!.confidence as number) : 0,
      schemaVersion: Number.isFinite(raw?.schemaVersion as number) ? (raw!.schemaVersion as number) : 0,
    };
  } catch (e) {
    if (axios.isCancel?.(e)) throw e;
    console.warn('[api] getFibGoldenZoneOverlay failed', e);
    return { ...EMPTY_FIB_GOLDEN_ZONE_OVERLAY, reason: 'fetch_failed' };
  }
};

export const getDarvasBoxOverlay = async (
  symbol: string,
  timeframe: 'DAILY' | 'WEEKLY' = 'DAILY',
  signal?: AbortSignal
): Promise<DarvasBoxOverlayResponse> => {
  if (!symbol) return { ...EMPTY_DARVAS_BOX_OVERLAY };
  try {
    const enc = encodeURIComponent(symbol.toUpperCase());
    const tf = timeframe === 'WEEKLY' ? 'WEEKLY' : 'DAILY';
    // Backend emits `time` as UNIX seconds already - pass through verbatim.
    const raw = await makeRequest<Partial<DarvasBoxOverlayResponse>>(
      'GET',
      `/api/deep-analysis/darvas-box?symbol=${enc}&timeframe=${tf}`,
      undefined,
      {
        cacheTTL: 60_000,
        debounce: 250,
        retries: 1,
        ...(signal ? { signal } : {}),
      }
    );
    return {
      detected: raw?.detected === true,
      status: raw?.status ?? 'none',
      patternQuality: raw?.patternQuality ?? 'none',
      overlay: raw?.overlay ?? null,
      rejectReasons: Array.isArray(raw?.rejectReasons) ? raw!.rejectReasons! : [],
      reason: typeof raw?.reason === 'string' ? raw!.reason! : '',
      confidence: Number.isFinite(raw?.confidence as number) ? (raw!.confidence as number) : 0,
      schemaVersion: Number.isFinite(raw?.schemaVersion as number) ? (raw!.schemaVersion as number) : 0,
    };
  } catch (e) {
    if (axios.isCancel?.(e)) throw e;
    console.warn('[api] getDarvasBoxOverlay failed', e);
    return { ...EMPTY_DARVAS_BOX_OVERLAY, reason: 'fetch_failed' };
  }
};

export const getStrongSupportOverlay = async (
  symbol: string,
  timeframe: 'DAILY' | 'WEEKLY' = 'DAILY',
  signal?: AbortSignal
): Promise<StrongSupportOverlayResponse> => {
  if (!symbol) return { ...EMPTY_STRONG_SUPPORT_OVERLAY };
  try {
    const enc = encodeURIComponent(symbol.toUpperCase());
    const tf = timeframe === 'WEEKLY' ? 'WEEKLY' : 'DAILY';
    // Backend emits `time` as UNIX seconds already - pass through verbatim.
    const raw = await makeRequest<Partial<StrongSupportOverlayResponse>>(
      'GET',
      `/api/deep-analysis/strong-support-line?symbol=${enc}&timeframe=${tf}`,
      undefined,
      {
        cacheTTL: 60_000,
        debounce: 250,
        retries: 1,
        ...(signal ? { signal } : {}),
      }
    );
    return {
      detected: raw?.detected === true,
      status: raw?.status ?? 'none',
      overlay: raw?.overlay ?? null,
      rejectReasons: Array.isArray(raw?.rejectReasons) ? raw!.rejectReasons! : [],
      reason: typeof raw?.reason === 'string' ? raw!.reason! : '',
      confidence: Number.isFinite(raw?.confidence as number) ? (raw!.confidence as number) : 0,
      schemaVersion: Number.isFinite(raw?.schemaVersion as number) ? (raw!.schemaVersion as number) : 0,
    };
  } catch (e) {
    if (axios.isCancel?.(e)) throw e;
    console.warn('[api] getStrongSupportOverlay failed', e);
    return { ...EMPTY_STRONG_SUPPORT_OVERLAY, reason: 'fetch_failed' };
  }
};

// Sparkline data for mini charts
export interface SparklineResponse {
  ticker: string;
  prices: number[];
  timestamps?: string[];
  success: boolean;
  error?: string;
}

export const getSparkline = async (
  ticker: string,
  timeframe: '1D' | '5D' | '1M' | '3M' = '1D'
): Promise<SparklineResponse> => {
  try {
    if (!ticker) {
      throw new Error('Ticker symbol is required');
    }
    const upperTicker = ticker.toUpperCase();
    const response = await api.get(`/api/sparkline/${upperTicker}`, {
      params: { timeframe },
    });
    return response.data;
  } catch (error: any) {
    // Return error response instead of throwing, so component can handle gracefully
    return {
      ticker: ticker.toUpperCase(),
      prices: [],
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to fetch sparkline data'
    };
  }
};

// AI Analysis
export const analyzeStocks = async (tickers: string[], analysisType: string = 'investment', signal?: AbortSignal): Promise<AIAnalysisData> => {
  try {
    if (!tickers || tickers.length === 0) {
      throw new Error('At least one ticker symbol is required');
    }
    return await makeRequest<AIAnalysisData>('POST', '/perplexity/analyze_multiple', {
      tickers,
      analysis_type: analysisType,
    }, {
      ...(signal && { signal })
    });
  } catch (error: any) {
    throw new Error(`Failed to analyze stocks: ${error.message}`);
  }
};

export const compareStocks = async (tickers: string[]): Promise<AIAnalysisData> => {
  try {
    if (!tickers || tickers.length < 2) {
      throw new Error('At least two ticker symbols are required for comparison');
    }
    return await makeRequest<AIAnalysisData>('POST', '/perplexity/compare', {
      tickers,
    });
  } catch (error: any) {
    throw new Error(`Failed to compare stocks: ${error.message}`);
  }
};

export const analyzeStockColor = async (tickers: string[]): Promise<ColorAnalysisData> => {
  try {
    if (!tickers || tickers.length === 0) {
      throw new Error('At least one ticker symbol is required');
    }
    return await makeRequest<ColorAnalysisData>('POST', '/perplexity/analyze_multiple_color', {
      tickers,
    });
  } catch (error: any) {
    throw new Error(`Failed to analyze stocks with color recommendations: ${error.message}`);
  }
};

export const getQuickInsight = async (ticker: string): Promise<AIAnalysisData> => {
  try {
    if (!ticker) {
      throw new Error('Ticker symbol is required');
    }
    return await makeRequest<AIAnalysisData>('POST', '/perplexity/quick_insight', {
      ticker: ticker.toUpperCase(),
    });
  } catch (error: any) {
    throw new Error(`Failed to get quick insight: ${error.message}`);
  }
};

export const analyzeStockStructured = async (ticker: string): Promise<ImageAnalysisData> => {
  try {
    if (!ticker) {
      throw new Error('Ticker symbol is required');
    }

    // Use the shared axios instance so the Firebase Bearer token is attached
    // by the request interceptor. Cache-buster kept in the URL so the
    // interceptor doesn't append a duplicate `_t`. axios throws on non-2xx.
    const response = await api.post(
      `/perplexity/analyze_structured?_t=${Date.now()}`,
      { ticker: ticker.toUpperCase() }
    );

    const result = response.data;

    if (result.success) {
      return {
        result: result,
        success: true
      };
    } else {
      throw new Error(result.error || 'Structured analysis failed');
    }
  } catch (error: any) {
    // axios surfaces the backend's JSON `error` field under
    // error.response.data.error; fall back to the generic message.
    const detail = error?.response?.data?.error || error?.message;
    throw new Error(`Failed to analyze stock: ${detail}`);
  }
};

export const analyzeStockWithImage = async (ticker: string, imageFile: File): Promise<ImageAnalysisData> => {
  try {
    if (!ticker) {
      throw new Error('Ticker symbol is required');
    }
    if (!imageFile) {
      throw new Error('Image file is required');
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(imageFile.type)) {
      throw new Error(`Unsupported file type. Allowed types: ${allowedTypes.join(', ')}`);
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (imageFile.size > maxSize) {
      throw new Error(`Image file too large. Maximum size is ${maxSize / (1024 * 1024)}MB.`);
    }

    // Create FormData
    const formData = new FormData();
    formData.append('ticker', ticker.toUpperCase());
    formData.append('image', imageFile);

    // Use the shared axios instance so the Firebase Bearer token is attached
    // by the request interceptor. The instance default Content-Type is
    // application/json (api.ts:200), which makes axios JSON-stringify a
    // FormData body and drop the file - so multipart/form-data MUST be set
    // explicitly here (matches uploadStockCsv). Cache-buster kept in the URL
    // so the interceptor doesn't append a duplicate `_t`.
    const response = await api.post(
      `/perplexity/analyze_with_image?_t=${Date.now()}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    const result = response.data;

    if (result.success) {
      return {
        result: result,
        success: true
      };
    } else {
      throw new Error(result.error || 'Image analysis failed');
    }
  } catch (error: any) {
    // axios surfaces the backend's JSON `error` field under
    // error.response.data.error; fall back to the generic message.
    const detail = error?.response?.data?.error || error?.message;
    throw new Error(`Failed to analyze stock with image: ${detail}`);
  }
};

// Chart snapshot analysis for pattern detection
export const analyzeChartSnapshot = async (imageFile: File): Promise<ChartSnapshotResult> => {
  try {
    if (!imageFile) {
      throw new Error('Image file is required');
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(imageFile.type)) {
      throw new Error('Unsupported file type. Please use PNG, JPG, GIF, or WebP images.');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (imageFile.size > maxSize) {
      throw new Error('File size must be less than 10MB');
    }

    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('analysis_type', 'pattern_detection');

    // Use the shared axios instance so the Firebase Bearer token is attached
    // by the request interceptor. The instance default Content-Type is
    // application/json (api.ts:200), which makes axios JSON-stringify a
    // FormData body and drop the file - so multipart/form-data MUST be set
    // explicitly here (matches uploadStockCsv). Cache-buster kept in the URL
    // so the interceptor doesn't append a duplicate `_t`. axios throws on
    // non-2xx; the catch below converts that to the {success:false} contract.
    const response = await api.post(
      `/analyze_chart_patterns?_t=${Date.now()}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    const result = response.data;

    if (result.success) {
      return {
        success: true,
        analysis: result.analysis,
        processing_time: result.processing_time
      };
    } else {
      return {
        success: false,
        error: result.error || 'Chart analysis failed'
      };
    }
  } catch (error: any) {
    // axios surfaces the backend's JSON `error` field under
    // error.response.data.error; fall back to the generic message.
    return {
      success: false,
      error: error?.response?.data?.error || error?.message || 'Failed to analyze chart snapshot'
    };
  }
};

// Status polling
export const getScreeningStatus = async (): Promise<ScreeningStatus> => {
  return makeRequest<ScreeningStatus>('GET', '/status');
};



export const pollScreeningStatus = async (): Promise<ScreeningStatus> => {
  try {
    // Keep polling payload lightweight; full results are fetched from /results.
    const response = await api.get('/screening_status?include_results=0');
    console.log('📡 Screening Status Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error polling screening status:', error);
    throw error;
  }
};

// Favorites API endpoints
export const getFavorites = async (signal?: AbortSignal): Promise<Favorite[]> => {
  try {
    const response = await makeRequest<{success: boolean, favorites: Favorite[], count: number}>('GET', '/api/favorites', undefined, {
      cacheTTL: 120000, // 2 minute cache
      debounce: 300,
      ...(signal && { signal })
    });
    // Extract favorites array from the response structure
    return response.favorites || [];
  } catch (error: any) {
    throw new Error(`Failed to fetch favorites: ${error.message}`);
  }
};

export const addFavorite = async (ticker: string, color: string, notes: string = ''): Promise<Favorite> => {
  try {
    const requestData = {
      ticker: ticker.toUpperCase(),
      color,
      notes,
    };
    const response = await makeRequest<{success: boolean, message: string, favorite: Favorite}>('POST', '/api/favorites', requestData);
    // Extract favorite from the response structure
    return response.favorite;
  } catch (error: any) {
    throw new Error(`Failed to add favorite: ${error.message}`);
  }
};

export const updateFavorite = async (ticker: string, color?: string, notes?: string): Promise<Favorite> => {
  try {
    const requestData: any = {
      ticker: ticker.toUpperCase(),
    };
    if (color !== undefined) requestData.color = color;
    if (notes !== undefined) requestData.notes = notes;

    const response = await makeRequest<{ success: boolean; favorite: Favorite }>(
      'PUT',
      `/api/favorites/${ticker.toUpperCase()}`,
      requestData
    );
    return response.favorite;
  } catch (error: any) {
    throw new Error(`Failed to update favorite: ${error.message}`);
  }
};

export const deleteFavorite = async (ticker: string): Promise<void> => {
  try {
    await makeRequest<void>('DELETE', `/api/favorites/${ticker.toUpperCase()}`);
  } catch (error: any) {
    throw new Error(`Failed to delete favorite: ${error.message}`);
  }
};

export const reorderFavorites = async (tickerOrder: string[]): Promise<Favorite[]> => {
  try {
    const requestData = {
      ticker_order: tickerOrder.map(ticker => ticker.toUpperCase()),
    };
    return await makeRequest<Favorite[]>('POST', '/api/favorites/reorder', requestData);
  } catch (error: any) {
    throw new Error(`Failed to reorder favorites: ${error.message}`);
  }
};

// Buy Signal Analysis API
export const analyzeBuySignals = async (tickers: string[]): Promise<BuySignalAnalysisResult> => {
  try {
    const requestData = {
      tickers: tickers.map(ticker => ticker.toUpperCase()),
    };

    const cacheKey = createCacheKey('POST', '/api/analyze_buy_signals', requestData);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Returning cached buy signals analysis');
      return cachedData;
    }

    console.log(`Analyzing buy signals for ${tickers.length} tickers:`, tickers);

    const response = await makeRequest<BuySignalAnalysisResult>(
      'POST',
      '/api/analyze_buy_signals',
      requestData,
      {
        timeout: 120000, // 2 minute timeout for AI analysis
        cacheTTL: 600000, // 10 minute cache - AI analysis changes less frequently
        skipCache: false
      }
    );

    // Cache the successful response
    if (response.success) {
      setCachedData(cacheKey, response, 600000); // 10 minute cache
    }

    return response;
  } catch (error: any) {
    console.error('Buy signals analysis failed:', error);
    throw new Error(`Failed to analyze buy signals: ${error.message}`);
  }
};

export const analyzeSingleBuySignal = async (ticker: string): Promise<SingleBuySignalResult> => {
  try {
    const upperTicker = ticker.toUpperCase();

    const cacheKey = createCacheKey('GET', `/api/analyze_buy_signal/${upperTicker}`);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log(`Returning cached buy signal for ${upperTicker}`);
      return cachedData;
    }

    console.log(`Analyzing buy signal for ${upperTicker}`);

    const response = await makeRequest<SingleBuySignalResult>(
      'GET',
      `/api/analyze_buy_signal/${upperTicker}`,
      undefined,
      {
        timeout: 60000, // 1 minute timeout for single analysis
        cacheTTL: 300000, // 5 minute cache for single stock
        skipCache: false
      }
    );

    // Cache the successful response
    if (response.success) {
      setCachedData(cacheKey, response, 300000); // 5 minute cache
    }

    return response;
  } catch (error: any) {
    console.error(`Buy signal analysis failed for ${ticker}:`, error);
    throw new Error(`Failed to analyze buy signal for ${ticker}: ${error.message}`);
  }
};

// Helper function to merge buy signal data with stock data
export const mergeBuySignalsWithStocks = (
  stocks: StockData[],
  buySignalResults: BuySignalAnalysisResult
): StockData[] => {
  if (!buySignalResults.success || !buySignalResults.results) {
    return stocks;
  }

  // Create a map for quick lookup of buy signals by ticker
  const buySignalMap = new Map<string, any>();
  console.log('📋 Processing buy signal results:', buySignalResults.results?.length || 0, 'results');

  buySignalResults.results.forEach((result: any, index: number) => {
    if (result.success && result.ticker) {
      const mappedData = {
        current_price: result.current_price,
        buy_score: result.score || result.buy_score || 0, // Handle both new and old API formats
        explanation: result.explanation,
        confidence: result.confidence,
        success: true,
        raw_content: result.raw_content,
        model: result.model,
        attempts: result.attempts
      };
      buySignalMap.set(result.ticker.toUpperCase(), mappedData);
      console.log(`✅ Mapped ${result.ticker}: Score ${result.score || result.buy_score}/10`);
    } else if (result.ticker) {
      const failedData = {
        current_price: 0,
        buy_score: 0,
        explanation: result.error || 'Analysis failed',
        confidence: 'low' as const,
        success: false,
        error: result.error
      };
      buySignalMap.set(result.ticker.toUpperCase(), failedData);
      console.log(`❌ Failed ${result.ticker}: ${result.error}`);
    }
  });

  // Merge buy signal data with stock data
  return stocks.map(stock => ({
    ...stock,
    ai_buy_signal: buySignalMap.get(stock.ticker.toUpperCase())
  }));
};


// Data Update API
export const forceDataUpdate = async (ignoreMarketHours: boolean = false): Promise<{success: boolean, message: string}> => {
  try {
    const response = await makeRequest<{success: boolean, message: string, status: string}>(
      'POST',
      '/api/auto-updater/force-update',
      { ignore_market_hours: ignoreMarketHours },
      {
        timeout: 180000, // 3 minute timeout for data updates
        retries: 1
      }
    );

    return {
      success: response.success,
      message: response.message
    };
  } catch (error: any) {
    // Extract user-friendly error message from the backend response
    if (error.message.includes('Too early - next update in')) {
      throw new Error(`Market hours restriction: ${error.message}`);
    }
    throw new Error(`Failed to update data: ${error.message}`);
  }
};

export default api;

// Named alias used by the chart-watermark hook + future call sites that
// prefer named imports over the default (TS noEmit fix 2026-05-09 - CI
// flagged `useChartWatermark.ts` importing a non-existent `apiClient`).
export { api as apiClient };

// Admin: CSV upload
export const uploadStockCsv = async (file: File): Promise<{ success: boolean; message?: string; stock_count?: number; filepath?: string }> => {
  const form = new FormData();
  form.append('csvFile', file);

  // Use axios instance to get proper auth headers
  const response = await api.post('/upload_csv', form, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const data = response.data;
  if (data.error) {
    throw new Error(data.error || 'CSV upload failed');
  }
  return { success: true, message: data.message, stock_count: data.stock_count, filepath: data.filepath };
};

// Admin: start update with mode flags
export interface ForceUpdateOptions {
  mode: 'all' | 'default' | 'csv';
  csvPath?: string | null;
  ignoreMarketHours?: boolean;
  missingOnly?: boolean;
  parallel?: boolean;
}

export const startDataUpdate = async (options: ForceUpdateOptions): Promise<{ success: boolean; message: string }> => {
  const body: any = {
    ignore_market_hours: !!options.ignoreMarketHours,
    use_all: options.mode === 'all',
    use_csv: options.mode === 'csv',
    csv_path: options.csvPath || null,
  };
  const params = new URLSearchParams();
  if (options.missingOnly) params.set('missing_only', 'true');
  if (options.parallel) params.set('parallel', 'true');
  params.set('_t', Date.now().toString());

  const queryStr = params.toString();
  const endpoint = `/api/auto-updater/force-update${queryStr ? `?${queryStr}` : ''}`;

  const resp = await api.post(endpoint, body);
  const data = resp.data;
  if (data.error) {
    throw new Error(data.error || 'Failed to start data update');
  }
  return { success: true, message: data.message || 'Data update started' };
};

// Load index data from CSV files
export const loadIndexDataFromCsv = async (): Promise<{ success: boolean; message?: string; stats?: Record<string, number>; total_loaded?: number; warnings?: string[] }> => {
  return makeRequest<{ success: boolean; message?: string; stats?: Record<string, number>; total_loaded?: number; warnings?: string[] }>('POST', '/api/index/load-csv');
};

// Get index statistics
export const getIndexStatistics = async (): Promise<{ success: boolean; stats?: Record<string, number>; total_stocks?: number; error?: string }> => {
  return makeRequest<{ success: boolean; stats?: Record<string, number>; total_stocks?: number; error?: string }>('GET', '/api/index/statistics');
};

// Admin: poll combined status
export const getSystemStatus = async (): Promise<StatusResponse> => {
  // Poll lightweight status to avoid frequent DB queries during progress polling.
  return makeRequest<StatusResponse>('GET', '/status?include_db=0', undefined, {
    debounce: 300,
    cacheTTL: 2000,
    skipCache: true,
    timeout: 12000,
    retries: 0,
  });
};

// ==========================================
// User Permissions API Functions
// ==========================================

// Get all users (admin only)
export const getUsers = async (filters?: UserManagementFilters): Promise<{ users: User[]; total: number }> => {
  const params = new URLSearchParams();
  if (filters?.role) params.append('role', filters.role);
  if (filters?.emailVerified !== undefined) params.append('emailVerified', filters.emailVerified.toString());
  if (filters?.disabled !== undefined) params.append('disabled', filters.disabled.toString());
  if (filters?.search) params.append('search', filters.search);

  const queryString = params.toString();
  const url = queryString ? `/api/admin/users?${queryString}` : '/api/admin/users';

  return makeRequest<{ users: User[]; total: number }>('GET', url);
};

// Get admin statistics
export const getAdminStats = async (): Promise<AdminStats> => {
  const response = await makeRequest<{ success: boolean; stats: AdminStats }>(
    'GET',
    '/api/admin/stats',
    undefined,
    { cacheTTL: 30000 }
  );
  return response.stats;
};

// Manage user roles (admin only)
export const manageUserRole = async (options: RoleManagementOptions): Promise<{ success: boolean; message: string }> => {
  // Get user data to find email address
  const usersResponse = await getUsers();
  const user = usersResponse.users.find(u => u.uid === options.userId);

  if (!user || !user.email) {
    throw new Error('User not found or email not available');
  }

  // Map frontend action to backend action
  const backendAction = options.action === 'add' ? 'grant' : 'revoke';

  // Build the request body for the backend
  const requestBody = {
    action: backendAction,
    role: options.role,
    email: user.email
  };

  // Call the correct backend endpoint
  return makeRequest<{ success: boolean; message: string }>('POST', `/api/users/${options.userId}/roles`, requestBody);
};

// Get role requests
export const getRoleRequests = async (filters?: RoleRequestFilters): Promise<{ requests: RoleRequest[]; total: number }> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.role) params.append('role', filters.role);
  if (filters?.search) params.append('search', filters.search);

  const queryString = params.toString();
  const url = queryString ? `/api/admin/role-requests?${queryString}` : '/api/admin/role-requests';

  return makeRequest<{ requests: RoleRequest[]; total: number }>('GET', url);
};

// Submit role request (authenticated user)
export const submitRoleRequest = async (requestedRole: string, reason: string): Promise<{ success: boolean; message: string }> => {
  return makeRequest<{ success: boolean; message: string }>('POST', '/api/user/role-requests', {
    role: requestedRole,
    reason
  });
};

// Review role request (admin only)
export const reviewRoleRequest = async (
  requestId: string,
  approved: boolean,
  reviewNotes?: string
): Promise<{ success: boolean; message: string }> => {
  return makeRequest<{ success: boolean; message: string }>('POST', `/api/role-requests/${requestId}/review`, {
    action: approved ? 'approve' : 'reject',
    notes: reviewNotes || ''
  });
};

// Get current user's role requests
export const getUserRoleRequests = async (): Promise<{ requests: RoleRequest[] }> => {
  return makeRequest<{ requests: RoleRequest[] }>('GET', '/api/user/role-requests');
};

// Available roles list
export const getAvailableRoles = async (): Promise<{ roles: string[] }> => {
  return makeRequest<{ roles: string[] }>('GET', '/api/roles', undefined, { cacheTTL: 300000 });
};

// Dismiss data update completion notification
export const dismissUpdateNotification = async (): Promise<{ success: boolean }> => {
  return makeRequest<{ success: boolean }>('POST', '/api/data-update/dismiss-notification');
};

// Grok Sentiment Analysis
export const analyzeTickerWithGrok = async (
  ticker: string,
  signal?: AbortSignal,
  forceRefresh: boolean = false
): Promise<GrokSentimentResponse> => {
  try {
    if (!ticker) {
      throw new Error('Ticker symbol is required');
    }
    return await makeRequest<GrokSentimentResponse>(
      'POST',
      '/api/grok/analyze_ticker',
      {
        ticker: ticker.toUpperCase(),
        force_refresh: forceRefresh
      },
      { ...(signal && { signal }) }
    );
  } catch (error: any) {
    throw new Error(`Failed to analyze ticker with Grok: ${error.message}`);
  }
};

export const analyzeBatchWithGrok = async (
  tickers: string[],
  signal?: AbortSignal,
  forceRefresh: boolean = false
): Promise<GrokBatchSentimentResponse> => {
  try {
    if (!tickers || tickers.length === 0) {
      throw new Error('At least one ticker symbol is required');
    }
    return await makeRequest<GrokBatchSentimentResponse>(
      'POST',
      '/api/grok/analyze_batch',
      {
        tickers: tickers.map(t => t.toUpperCase()),
        force_refresh: forceRefresh
      },
      { ...(signal && { signal }) }
    );
  } catch (error: any) {
    throw new Error(`Failed to analyze batch with Grok: ${error.message}`);
  }
};

export const getGrokStatus = async (): Promise<{ available: boolean; configured: boolean }> => {
  try {
    return await makeRequest<{ available: boolean; configured: boolean }>(
      'GET',
      '/api/grok/status'
    );
  } catch (error: any) {
    return { available: false, configured: false };
  }
};

// Perplexity Stock Analysis (Structured)
export const analyzeStockWithPerplexity = async (
  ticker: string,
  includeChartContext: boolean = false,
  signal?: AbortSignal,
  forceRefresh: boolean = false
): Promise<PerplexityStockAnalysisResponse> => {
  try {
    if (!ticker) {
      throw new Error('Ticker symbol is required');
    }
    return await makeRequest<PerplexityStockAnalysisResponse>(
      'POST',
      '/api/analysis/perplexity/stock',
      {
        ticker: ticker.toUpperCase(),
        include_chart_context: includeChartContext,
        force_refresh: forceRefresh,
      },
      { ...(signal && { signal }) }
    );
  } catch (error: any) {
    throw new Error(`Failed to analyze stock with Perplexity: ${error.message}`);
  }
};

export const analyzeStockWithPerplexityChart = async (
  ticker: string,
  chartImageFile: File,
  signal?: AbortSignal
): Promise<PerplexityStockAnalysisResponse> => {
  try {
    if (!ticker) {
      throw new Error('Ticker symbol is required');
    }
    if (!chartImageFile) {
      throw new Error('Chart image file is required');
    }

    const formData = new FormData();
    formData.append('ticker', ticker.toUpperCase());
    formData.append('chart_image', chartImageFile);

    // Use the shared axios instance so the Firebase Bearer token is attached
    // by the request interceptor. The instance default Content-Type is
    // application/json (api.ts:200), which makes axios JSON-stringify a
    // FormData body and drop the file - so multipart/form-data MUST be set
    // explicitly here (matches uploadStockCsv). Cache-buster kept in the URL
    // so the interceptor doesn't append a duplicate `_t`. axios throws on
    // non-2xx and forwards the AbortSignal for cancellation.
    const response = await api.post(
      `/api/analysis/perplexity/stock-with-chart?_t=${Date.now()}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, ...(signal && { signal }) }
    );

    const result = response.data;
    return result;
  } catch (error: any) {
    // axios surfaces the backend's JSON `error` field under
    // error.response.data.error; fall back to the generic message.
    const detail = error?.response?.data?.error || error?.message;
    throw new Error(`Failed to analyze stock with chart: ${detail}`);
  }
};

// ============================================================================
// Market indices (landing widgets)
// ============================================================================

export interface MarketIndexCandle {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export interface MarketIndexPayload {
  key: string;
  label: string;
  symbol: string;
  dateLabel: string;
  last: number | null;
  change: number | null;
  changePercent: number | null;
  previousClose: number | null;
  relativeVolume: number | null;
  candles: MarketIndexCandle[];
  error: string | null;
}

export const getMarketIndices = async (): Promise<{
  indices: MarketIndexPayload[];
  asOf: string | null;
}> => {
  return makeRequest('GET', '/api/market_indices', undefined, { skipCache: true, cacheTTL: 60_000 });
};

// ============================================================================
// Billing API
// ============================================================================

export interface SubscriptionPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice?: number;
  /** Stripe Price ID for checkout (preferred over id when present) */
  stripePriceId?: string;
  /** Backend billing_plans code (free | pro_monthly | pro_yearly | admin_grant) */
  code?: string;
  /** Billing interval (month | year) for the billing_plans catalog */
  interval?: string;
  /** PayPal provider plan id (P-xxx) */
  providerPlanId?: string;
  aiCredits: number;
  maxFavorites: number;
  maxScansDaily?: number;
  trialDays: number;
  features: string[];
}

/** Entitlement summary from GET /api/billing/me */
export interface BillingMe {
  plan_code: string;
  entitlement_tier: string;
  status: string;
  provider: string | null;
  is_trialing: boolean;
  is_comped: boolean;
  is_expired?: boolean;
  pending_verification: boolean;
  trial_ends_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  checkoutProvider: 'paypal' | 'stripe';
  limits: {
    daily_scans: number | null;
    max_favorites: number;
    ai_credits_monthly: number;
    csv_export?: boolean;
    all_filters?: boolean;
  };
}

export interface PayPalSubscriptionContext {
  provider: string;
  plan_id: string;
  public_plan_code: string;
  client_id: string;
  currency: string;
  /** The authenticated user's id - passed as custom_id when creating the PayPal
   *  subscription so webhooks can map it back to the user. */
  custom_id: string;
}

/** DB/JSON often sends DECIMAL as string; keep UI math safe */
function parseSubscriptionPlan(raw: Record<string, unknown>): SubscriptionPlan {
  const n = (v: unknown, fallback = 0): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const x = Number(v);
      return Number.isFinite(x) ? x : fallback;
    }
    return fallback;
  };
  const nOpt = (v: unknown): number | undefined => {
    if (v == null || v === '') return undefined;
    const x = typeof v === 'number' ? v : Number(String(v));
    return Number.isFinite(x) ? x : undefined;
  };
  const featuresRaw = raw.features;
  let features: string[] = [];
  if (Array.isArray(featuresRaw)) {
    features = featuresRaw.map((x) => String(x));
  } else if (typeof featuresRaw === 'string') {
    try {
      const parsed = JSON.parse(featuresRaw || '[]');
      features = Array.isArray(parsed) ? parsed.map((x: unknown) => String(x)) : [];
    } catch {
      features = [];
    }
  }

  const yearlyPrice = nOpt(raw.yearlyPrice);
  const stripeRaw = typeof raw.stripePriceId === 'string' ? raw.stripePriceId.trim() : '';
  const maxScansDaily = nOpt(raw.maxScansDaily);

  const plan: SubscriptionPlan = {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    monthlyPrice: n(raw.monthlyPrice),
    aiCredits: Math.round(n(raw.aiCredits)),
    maxFavorites: Math.round(n(raw.maxFavorites)),
    trialDays: Math.round(n(raw.trialDays)),
    features,
  };
  if (yearlyPrice !== undefined) {
    plan.yearlyPrice = yearlyPrice;
  }
  if (stripeRaw) {
    plan.stripePriceId = stripeRaw;
  }
  if (maxScansDaily !== undefined) {
    plan.maxScansDaily = maxScansDaily;
  }
  if (typeof raw.code === 'string' && raw.code) plan.code = raw.code;
  if (typeof raw.interval === 'string' && raw.interval) plan.interval = raw.interval;
  if (typeof raw.providerPlanId === 'string' && raw.providerPlanId) {
    plan.providerPlanId = raw.providerPlanId;
  }
  return plan;
}

export interface Subscription {
  planId: string;
  planName: string;
  status: string;
  trialEnd?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  freeScansRemaining: number;
}

export interface UserCredits {
  balance: number;
  dailyFreeRemaining: number;
  total: number;
  dailyFreeResetAt?: string;
}

export interface CreditTransaction {
  id: number;
  amount: number;
  reason: string;
  reference?: string;
  balanceAfter: number;
  createdAt: string;
}

export const getSubscriptionPlans = async (): Promise<{ plans: SubscriptionPlan[] }> => {
  const response = await api.get('/api/billing/plans');
  const data = response.data as { plans?: unknown[] };
  const rawPlans = Array.isArray(data.plans) ? data.plans : [];
  return {
    plans: rawPlans.map((p) => parseSubscriptionPlan(p as Record<string, unknown>)),
  };
};

export const createCheckoutSession = async (priceId?: string, trialDays: number = 7): Promise<{ url: string; sessionId: string }> => {
  const response = await api.post('/api/billing/checkout', { priceId, trialDays });
  return response.data;
};

export const createPortalSession = async (): Promise<{ url: string }> => {
  const response = await api.post('/api/billing/portal');
  return response.data;
};

export const getSubscription = async (): Promise<{ subscription: Subscription; plan: string; status: string; freeScansRemaining: number }> => {
  const response = await api.get('/api/billing/subscription');
  return response.data;
};

export const cancelSubscription = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/api/billing/subscription/cancel');
  return response.data;
};

export const getCredits = async (): Promise<UserCredits> => {
  const response = await api.get('/api/billing/credits');
  return response.data;
};

export const deductCredits = async (amount: number, reason: string, reference?: string): Promise<{ success: boolean; deducted: boolean; balance: number }> => {
  const response = await api.post('/api/billing/credits/deduct', { amount, reason, reference });
  return response.data;
};

export const getCreditTransactions = async (limit: number = 50, offset: number = 0): Promise<{ transactions: CreditTransaction[] }> => {
  const response = await api.get('/api/billing/credits/transactions', { params: { limit, offset } });
  return response.data;
};

// --- PayPal billing (PR4) --------------------------------------------------

export const getBillingMe = async (): Promise<BillingMe> => {
  const response = await api.get('/api/billing/me');
  return response.data as BillingMe;
};

export const createPayPalSubscription = async (
  planCode: 'pro_monthly' | 'pro_yearly'
): Promise<PayPalSubscriptionContext> => {
  const response = await api.post('/api/billing/paypal/create-subscription', { plan_code: planCode });
  return response.data as PayPalSubscriptionContext;
};

export const syncBilling = async (subscriptionId?: string): Promise<{ success: boolean; me: BillingMe }> => {
  const response = await api.post('/api/billing/sync', { subscriptionID: subscriptionId });
  return response.data;
};

export const cancelPayPalSubscription = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/api/billing/paypal/cancel');
  return response.data;
};

// Admin complimentary access (PR5)
export const grantAdminFreeAccess = async (userId: string): Promise<{ success: boolean }> => {
  const response = await api.post(`/api/admin/users/${encodeURIComponent(userId)}/free-access`);
  return response.data;
};

export const revokeAdminFreeAccess = async (userId: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`/api/admin/users/${encodeURIComponent(userId)}/free-access`);
  return response.data;
};

export const adminBillingResync = async (userId: string): Promise<{ success: boolean; me: BillingMe }> => {
  const response = await api.post(`/api/admin/billing/resync/${encodeURIComponent(userId)}`);
  return response.data;
};

export const adminBillingUserSnapshot = async (userId: string): Promise<{
  success: boolean;
  entitlement: BillingMe;
  subscription: Record<string, unknown> | null;
  recent_events: Array<Record<string, unknown>>;
}> => {
  const response = await api.get(`/api/admin/billing/users/${encodeURIComponent(userId)}`);
  return response.data;
};

export const getUsage = async (): Promise<{
  tier: string;
  isPaid: boolean;
  isTrialing: boolean;
  dailyScansLimit: number | null;
  freeScansRemaining: number;
  maxFavorites: number;
  aiCreditsIncluded: number;
  aiCreditsBalance: number;
}> => {
  const response = await api.get('/api/billing/usage');
  return response.data;
};

// MA Touch Alerts API
export const getMATouchAlertRules = async (includeDisabled: boolean = false): Promise<{ rules: MATouchAlertRule[] }> => {
  const response = await api.get('/api/alerts/ma-touch/rules', { params: { include_disabled: includeDisabled } });
  return response.data;
};

export const createMATouchAlertRule = async (payload: MATouchAlertRulePayload): Promise<{ rule: MATouchAlertRule }> => {
  const response = await api.post('/api/alerts/ma-touch/rules', payload);
  return response.data;
};

export const updateMATouchAlertRule = async (ruleId: string, payload: Partial<MATouchAlertRulePayload>): Promise<{ rule: MATouchAlertRule }> => {
  const response = await api.put(`/api/alerts/ma-touch/rules/${ruleId}`, payload);
  return response.data;
};

export const deleteMATouchAlertRule = async (ruleId: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`/api/alerts/ma-touch/rules/${ruleId}`);
  return response.data;
};

export const enableMATouchAlertRule = async (ruleId: string): Promise<{ rule: MATouchAlertRule }> => {
  const response = await api.post(`/api/alerts/ma-touch/rules/${ruleId}/enable`);
  return response.data;
};

export const disableMATouchAlertRule = async (ruleId: string): Promise<{ rule: MATouchAlertRule }> => {
  const response = await api.post(`/api/alerts/ma-touch/rules/${ruleId}/disable`);
  return response.data;
};

export const getMATouchAlertEvents = async (limit: number = 50, offset: number = 0): Promise<{ events: MATouchAlertEvent[] }> => {
  const response = await api.get('/api/alerts/ma-touch/events', { params: { limit, offset } });
  return response.data;
};

export const getMATouchAlertStatus = async (): Promise<MATouchAlertStatus> => {
  const response = await api.get('/api/alerts/ma-touch/status');
  return response.data;
};

export const sendMATouchTestEmail = async (email: string): Promise<{ success: boolean; message?: string }> => {
  const response = await api.post('/api/alerts/ma-touch/test-email', { email });
  return response.data;
};

export const getTelegramAlertStatus = async (): Promise<TelegramAlertStatus> => {
  const response = await api.get('/api/alerts/telegram/status');
  return response.data;
};

export const createTelegramConnectLink = async () => {
  const response = await api.post('/api/alerts/telegram/connect-link');
  return response.data;
};

export const sendTelegramTestMessage = async () => {
  const response = await api.post('/api/alerts/telegram/test');
  return response.data;
};

export const deleteTelegramConnection = async () => {
  const response = await api.delete('/api/alerts/telegram/connection');
  return response.data;
};

// WhatsApp Alert API
export const getWhatsAppAlertStatus = async (): Promise<WhatsAppAlertStatus> => {
  const response = await api.get('/api/alerts/whatsapp/status');
  return response.data;
};

export const createWhatsAppConnection = async (phoneNumber: string) => {
  const response = await api.post('/api/alerts/whatsapp/connect', { phone_number: phoneNumber });
  return response.data;
};

export const verifyWhatsAppConnection = async (code: string) => {
  const response = await api.post('/api/alerts/whatsapp/verify', { code });
  return response.data;
};

export const sendWhatsAppTestMessage = async () => {
  const response = await api.post('/api/alerts/whatsapp/test');
  return response.data;
};

export const deleteWhatsAppConnection = async () => {
  const response = await api.delete('/api/alerts/whatsapp/connection');
  return response.data;
};

// Alert Filter Config API
export const getAlertFilterConfig = async (ruleId: string) => {
  const response = await api.get(`/api/alerts/ma-touch/rules/${ruleId}/filters`);
  return response.data;
};

export const updateAlertFilterConfig = async (ruleId: string, config: Record<string, any>) => {
  const response = await api.put(`/api/alerts/ma-touch/rules/${ruleId}/filters`, config);
  return response.data;
};

export const cloneMainFiltersForAlert = async (ruleId: string) => {
  const response = await api.post(`/api/alerts/ma-touch/rules/${ruleId}/filters/clone-main`);
  return response.data;
};

// Alert State Machine API
export const getAlertStateMachineStatus = async (stateFilter?: string) => {
  const params = stateFilter ? `?state=${stateFilter}` : '';
  const response = await api.get(`/api/alerts/state-machine/status${params}`);
  return response.data;
};

// Buy-Action Alert API (one-shot buy-zone watchlist) -
// PLAN_BUY_ACTION_ALERT_2026-06-20. Mirrors the MA-touch method/typing shape
// above (axios via the shared `api` instance; Firebase Bearer + 401 refresh
// handled by the interceptor).
export const getBuyActionWatchlist = async (): Promise<{ watches: BuyActionWatch[] }> => {
  const response = await api.get('/api/alerts/buy-action/watchlist');
  return response.data;
};

export const createBuyActionWatch = async (
  payload: BuyActionWatchPayload
): Promise<{ watch: BuyActionWatch }> => {
  const response = await api.post('/api/alerts/buy-action/watch', payload);
  return response.data;
};

export const deleteBuyActionWatch = async (ticker: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`/api/alerts/buy-action/watch/${encodeURIComponent(ticker)}`);
  return response.data;
};

export const getBuyActionEvents = async (limit: number = 50): Promise<{ events: BuyActionEvent[] }> => {
  const response = await api.get('/api/alerts/buy-action/events', { params: { limit } });
  return response.data;
};

// Admin MA Touch Alerts API
export const getMATouchAlertHealth = async (): Promise<MATouchAlertHealth> => {
  const response = await api.get('/api/admin/alerts/ma-touch/health');
  return response.data;
};

export const replayMATouchAlert = async (date: string): Promise<ReplayResult | { error: string }> => {
  const response = await api.post('/api/admin/alerts/ma-touch/replay', { date });
  return response.data;
};

// Admin Alert Triage tab (Phase 4) - Plan i-want-in-the-nested-simon.md §4.
// Matches the call shape of `getMATouchAlertHealth` above (axios via
// shared `api` instance, no request interceptor changes).
export const getAdminAlertsUserStatus = async (): Promise<{
  users: AdminAlertsUserStatus[];
  generated_at: string;
}> => {
  const response = await api.get('/api/admin/alerts/users-status');
  return response.data;
};

export const getAdminAlertsLogs = async (
  opts: { lines?: number; level?: string } = {}
): Promise<AdminAlertsLogResponse> => {
  const response = await api.get('/api/admin/alerts/logs', { params: opts });
  return response.data;
};

export const runAdminAlertsE2ETest = async (): Promise<AdminE2ETestResult> => {
  const response = await api.post('/api/admin/alerts/test-end-to-end');
  return response.data;
};


// ============================================
// Admin Auth Events (signup telemetry)
// ============================================

export interface AdminSignupEvent {
  id: number;
  created_at: string | null;
  firebase_uid: string | null;
  email: string | null; // unmasked since 2026-05-19 per admin request
  provider: 'email' | 'google';
  verification_sent: boolean | null;
  error_code: string | null;
  error_message: string | null;
  ip: string | null;
  user_agent: string | null;
  display_name: string | null;
  photo_url: string | null;
  language: string | null;
  timezone: string | null;
  platform: string | null;
}

export const getAdminSignupEvents = async (
  opts: { limit?: number; failuresOnly?: boolean } = {}
): Promise<{ success: boolean; events: AdminSignupEvent[]; count: number }> => {
  const params: Record<string, string | number> = {};
  if (opts.limit) params.limit = opts.limit;
  if (opts.failuresOnly) params.failures_only = 'true';
  const response = await api.get('/api/admin/auth/signup-events', { params });
  return response.data;
};

/**
 * Fire-and-forget conversion-funnel telemetry (PR-C5 - Billing #2 conversion).
 *
 * Logs a funnel event (`pricing_view`, `upgrade_prompt_shown`,
 * `checkout_started`, `subscribed`) so admins can see drop-off. Callers
 * (PricingPage, UpgradePromptModal, checkout flow) import and call this.
 *
 * NEVER throws and never returns anything - telemetry must not block or
 * surface to the user. Uses a raw `fetch` with `keepalive` (not the axios
 * `api` instance) so it stays decoupled from the request queue and survives
 * the page navigation that often follows `checkout_started`/`subscribed`.
 *
 * @param event one of the funnel events above
 * @param meta  optional small context object (e.g. `{ reason: 'ai_analysis' }`).
 *              A top-level `reason` is lifted into its own column server-side.
 */
export function logConversionEvent(event: string, meta?: Record<string, unknown>): void {
  // Fire in a detached async IIFE so the public signature stays synchronous
  // `void` (C1/C2 callers never await it) while we can still resolve the
  // Firebase ID token asynchronously, exactly like `authedFetch` above.
  void (async () => {
    try {
      const base = getApiBaseUrl();
      const reason = meta && typeof meta.reason === 'string' ? (meta.reason as string) : undefined;
      const body: Record<string, unknown> = { event };
      if (reason) body.reason = reason;
      if (meta && Object.keys(meta).length > 0) body.meta = meta;

      // Attach the Firebase ID token when available so authenticated funnel
      // steps are attributed server-side; anonymous steps (e.g. a pricing_view
      // before sign-in) just omit it. Token retrieval never throws/blocks.
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      try {
        if (authService.isAvailable()) {
          const idToken = await authService.getIdToken();
          if (idToken) headers.Authorization = `Bearer ${idToken}`;
        }
      } catch {
        /* send unauthenticated rather than block telemetry */
      }

      await fetch(`${base}/api/conversion/event`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        // keepalive so the request survives the navigation that often follows
        // checkout_started / subscribed.
        keepalive: true,
      });
    } catch {
      /* swallow - telemetry never fails the user-facing flow */
    }
  })();
}


// ============================================
// Admin User Activity (currently-connected users)
// ============================================
// Backed by user_subscriptions.last_seen_at + Firebase
// last_sign_in_timestamp. See
// docs/superpowers/specs/2026-05-23-admin-user-activity-table-design.md.
//
// Types re-exported from ../types per the project convention that
// types live in src/types/index.ts (memory: reference_frontend_types_path);
// imported at the top of this file. Re-export keeps the existing import
// surface (`import { AdminUserActivity } from 'services/api'`) working.
export type {
  AdminUserActivity,
  AdminUserActivityResponse,
} from '../types';

export const getAdminUserActivity = async (
  opts: { onlineOnly?: boolean; limit?: number } = {}
): Promise<AdminUserActivityResponse> => {
  const params = new URLSearchParams();
  if (opts.onlineOnly) params.set('online_only', 'true');
  // `!= null` so callers passing limit=0 surface as an explicit 0
  // (server clamps it to 1). Plain `if (opts.limit)` would drop the 0
  // and silently apply the server default.
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const url = qs
    ? `/api/admin/users/activity?${qs}`
    : '/api/admin/users/activity';
  // Use makeRequest for retry + timeout + skipCache (this endpoint is
  // polled and must always fetch fresh).
  return makeRequest<AdminUserActivityResponse>('GET', url, undefined, {
    retries: 1,
    retryDelay: 1000,
    timeout: 10000,
    skipCache: true,
  });
};


// ============================================
// Disclaimer acceptance gate
// ============================================
// Server-enforced (backend stores `user_subscriptions.disclaimer_accepted_at`).
// Frontend reads status on auth resolve and shows a blocking modal when
// `accepted=false`. Closes the Google-signup race-bypass documented at
// firebase/index.ts:337-340.

export interface DisclaimerStatus {
  accepted: boolean;
  accepted_at: string | null;
}

export const getDisclaimerStatus = async (): Promise<DisclaimerStatus> => {
  return makeRequest<DisclaimerStatus>('GET', '/api/user/disclaimer-status',
    undefined,
    { retries: 1, retryDelay: 1000, timeout: 8000, skipCache: true },
  );
};

export const acceptDisclaimer = async (): Promise<DisclaimerStatus> => {
  return makeRequest<DisclaimerStatus>('POST', '/api/user/accept-disclaimer',
    {},
    { retries: 1, retryDelay: 1000, timeout: 8000, skipCache: true },
  );
};


// ============================================
// Admin Data Management API
// ============================================

// Get database statistics
export const getAdminDataStats = async (): Promise<AdminDataStats> => {
  return makeRequest<AdminDataStats>('GET', '/admin/data/stats?refresh=true', undefined, {
    timeout: 90000,
    retries: 1,
    retryDelay: 1500,
    skipCache: true,
  });
};

// Get all database tables with row counts and sample data
export const getDatabaseTables = async (): Promise<DatabaseTablesResponse> => {
  const response = await api.get('/admin/data/tables');
  return response.data;
};

// Get paginated data from a specific table
export const getTableData = async (
  tableName: string,
  page: number = 1,
  perPage: number = 50
): Promise<PaginatedTableData> => {
  try {
    const response = await api.get(`/admin/data/tables/${tableName}`, {
      params: { page, per_page: perPage }
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 403) {
      throw new Error(
        `Access denied for table "${tableName}". This is likely a protected system table.`
      );
    }
    throw error;
  }
};

// Get backend logs
export const getLogData = async (): Promise<AdminLogData> => {
  const response = await api.get('/admin/data/logs', { params: { format: 'json' } });
  return response.data;
};

// Download backend logs as file
export const downloadLogs = async (): Promise<Blob> => {
  const response = await api.get('/admin/data/logs', {
    params: { download: 'true' },
    responseType: 'blob'
  });
  return response.data;
};

// Clear stock data (preserves favorites, config, users)
export const clearStockData = async (): Promise<AdminActionResponse> => {
  const response = await api.post('/admin/data/clear-stock-data');
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to clear stock data');
  }
  return response.data;
};

// Clear ALL data (DANGEROUS - requires confirmation)
export const clearAllData = async (confirmPhrase: string): Promise<AdminActionResponse> => {
  const response = await api.post('/admin/data/clear-all', { confirm: confirmPhrase });
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to clear all data');
  }
  return response.data;
};

// Clear all user filter configs
export const clearAllConfigs = async (confirmPhrase: string): Promise<AdminActionResponse> => {
  const response = await api.delete('/admin/screen/config/clear-all', { data: { confirm: confirmPhrase } });
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to clear configs');
  }
  return response.data;
};

// Debug single ticker screening
export const debugTicker = async (ticker: string, userId?: string): Promise<DebugTickerResult> => {
  const params = userId ? { user_id: userId } : {};
  const response = await api.get(`/admin/screen/debug/${ticker}`, { params });
  return response.data;
};

// Get screening statistics
export const getScreeningStats = async (userId?: string): Promise<ScreeningStats> => {
  const params = userId ? { user_id: userId, sample_size: 25 } : { sample_size: 25 };
  const response = await api.get('/admin/screen/stats', {
    params,
    timeout: 25000,
  });
  return response.data;
};

// Get current user info (whoami)
export const getCurrentUser = async (): Promise<CurrentUser> => {
  const response = await api.get('/admin/screen/whoami');
  return response.data;
};

// Get user filter config
export const getUserConfig = async (userId: string): Promise<UserConfig> => {
  const response = await api.get(`/admin/screen/config/${userId}`);
  return response.data;
};

// Toggle auto-update
export const toggleAutoUpdate = async (enabled: boolean): Promise<{ success: boolean; enabled: boolean; message: string }> => {
  const response = await api.post('/admin/data/auto-update/toggle', { enabled });
  return response.data;
};

// Get auto-update status
export const getAutoUpdateStatus = async (): Promise<AutoUpdateStatus> => {
  const response = await api.get('/admin/data/auto-update/status');
  return response.data;
};

// Trigger stock data update
export const triggerDataUpdate = async (ignoreMarketHours: boolean = false): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/admin/data/update', { ignore_market_hours: ignoreMarketHours });
  return response.data;
};

// Get data update status
export const getDataUpdateStatus = async (): Promise<{ success: boolean; status: string; progress?: number }> => {
  const response = await api.get('/admin/data/status');
  return response.data;
};

// Terminate the running data update
export const terminateDataUpdate = async (): Promise<{ success: boolean; message?: string; error?: string }> => {
  const response = await api.post('/admin/data/update/terminate');
  return response.data;
};

// PR-6c - Force a full data rebuild (5y refetch for every ticker). Bypasses
// AUTO_UPDATE_MODE. Backend endpoint is CSRF-exempt + admin-gated and pins
// mode="full" + triggered_by="full_rebuild" on admin_data_state.
export const triggerFullRebuild = async (): Promise<{ success: boolean; message?: string; error?: string }> => {
  const response = await api.post('/admin/data/update/full-rebuild');
  return response.data;
};

// Upload CSV stock list
export const uploadStockList = async (file: File): Promise<{ success: boolean; message: string; stocks_added?: number }> => {
  const formData = new FormData();
  formData.append('csvFile', file);
  const response = await api.post('/admin/data/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// ============================================================================
// Autonomous Agents API
// ============================================================================

// Get all agents with status
export const getAgents = async (): Promise<AgentsResponse> => {
  const response = await api.get('/api/agents');
  return response.data;
};

// Get specific agent by type
export const getAgent = async (agentType: string): Promise<AgentResponse> => {
  const response = await api.get(`/api/agents/${agentType}`);
  return response.data;
};

// Start an agent
export const startAgent = async (agentType: string): Promise<AgentActionResponse> => {
  const response = await api.post(`/api/agents/${agentType}/start`);
  return response.data;
};

// Stop an agent
export const stopAgent = async (agentType: string): Promise<AgentActionResponse> => {
  const response = await api.post(`/api/agents/${agentType}/stop`);
  return response.data;
};

// Run an agent once
export const runAgent = async (agentType: string, checkTypes?: string[]): Promise<AgentRunResponse> => {
  const payload = checkTypes ? { check_types: checkTypes } : {};
  const response = await api.post(`/api/agents/${agentType}/run`, payload);
  return response.data;
};

// Get agent run history
export const getAgentHistory = async (agentType: string, limit: number = 50): Promise<AgentHistoryResponse> => {
  const response = await api.get(`/api/agents/${agentType}/history`, { params: { limit } });
  return response.data;
};

// ============================================================================
// Flag Agent API
// ============================================================================

export const getFlagAgents = async (): Promise<{ agents: FlagAgent[] }> => {
  const response = await api.get('/api/flag-agents');
  return response.data;
};

export const createFlagAgent = async (payload: Partial<FlagAgent>): Promise<{ agent: FlagAgent }> => {
  const response = await api.post('/api/flag-agents', payload);
  return response.data;
};

export const updateFlagAgent = async (id: string, payload: Partial<FlagAgent>): Promise<{ agent: FlagAgent }> => {
  const response = await api.put(`/api/flag-agents/${id}`, payload);
  return response.data;
};

export const deleteFlagAgent = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`/api/flag-agents/${id}`);
  return response.data;
};

export const getFlagAgentWithStates = async (id: string): Promise<{ agent: FlagAgent; states: FlagState[] }> => {
  const response = await api.get(`/api/flag-agents/${id}`);
  return response.data;
};

export const getFlagAgentStates = async (id: string): Promise<{ states: FlagState[] }> => {
  const response = await api.get(`/api/flag-agents/${id}/states`);
  return response.data;
};

export const runFlagAgentCheck = async (id: string): Promise<{ results: FlagAgentRunResult[]; errors: any[]; checked_at: string }> => {
  const response = await api.post(`/api/flag-agents/${id}/run`);
  return response.data;
};

export const getFlagConditions = async (): Promise<{ conditions: FlagConditionMeta[] }> => {
  const response = await api.get('/api/flag-agents/conditions');
  return response.data;
};

// ============================================================================
// SEPA (Smart Entry Point Analysis) API
// ============================================================================

// Get SEPA score for a symbol
export const getSEPAScore = async (symbol: string, market: string = 'us'): Promise<SEPAData> => {
  try {
    if (!symbol) {
      throw new Error('Ticker symbol is required');
    }
    const upperSymbol = symbol.toUpperCase();
    const response = await api.get(`/api/sepa/${upperSymbol}`, {
      params: { market },
    });
    // Convert snake_case API response to camelCase frontend type
    return convertSEPAResponse(response.data);
  } catch (error: any) {
    throw new Error(`Failed to get SEPA score for ${symbol}: ${error.message}`);
  }
};

// ============================================================================
// Pattern Detection API
// ============================================================================

// Get detected patterns for a symbol
export const getPatterns = async (
  symbol: string,
  market: string = 'us',
  lookbackDays: number = 90
): Promise<PatternDetectionData> => {
  try {
    if (!symbol) {
      throw new Error('Ticker symbol is required');
    }
    const upperSymbol = symbol.toUpperCase();
    const response = await api.get(`/api/patterns/${upperSymbol}`, {
      params: { market, lookback_days: lookbackDays },
    });
    // Convert snake_case API response to camelCase frontend type
    return convertPatternDetectionResult(response.data);
  } catch (error: any) {
    throw new Error(`Failed to get patterns for ${symbol}: ${error.message}`);
  }
};

// ============================================================================
// AI Analysis API
// ============================================================================

// Analyze a stock with AI (returns full analysis with SEPA score)
export const analyzeStock = async (
  symbol: string,
  market: string = 'us',
  options?: {
    includeSepa?: boolean;
    includePatterns?: boolean;
  }
): Promise<AIAnalysis> => {
  try {
    if (!symbol) {
      throw new Error('Ticker symbol is required');
    }
    const upperSymbol = symbol.toUpperCase();
    const response = await api.post('/api/analyze', {
      symbol: upperSymbol,
      market,
      include_sepa: options?.includeSepa ?? true,
      include_patterns: options?.includePatterns ?? false,
    });
    // Convert snake_case API response to camelCase frontend type
    return convertAIAnalysisResponse(response.data);
  } catch (error: any) {
    throw new Error(`Failed to analyze ${symbol}: ${error.message}`);
  }
};

// ============================================================================
// Finviz Screener API
// ============================================================================

export const fetchFinvizTickers = async (filters: FinvizFilterValues): Promise<FinvizScreenerResult> => {
  try {
    const response = await makeRequest<FinvizScreenerResult>(
      'POST',
      '/finviz_screener',
      filters,
      {
        timeout: 300000, // 5 minutes for large screener queries (3-exchange split)
        retries: 1,
        skipCache: true,
      }
    );
    return response;
  } catch (error: any) {
    console.error('Finviz screener error:', error);
    // Provide more descriptive error messages for network failures
    let errorMessage = 'Failed to fetch tickers from Finviz screener';
    if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      errorMessage = 'Cannot connect to server. Please check if the backend is running.';
    } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      errorMessage = 'Request timed out. Please try again.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    return {
      success: false,
      tickers: [],
      count: 0,
      error: errorMessage,
    };
  }
};

export interface FinvizFilterReferenceResponse {
  success: boolean;
  source?: string;
  filters: Record<string, { prefix: string; options: Record<string, string> }>;
  error?: string;
}

// Save Finviz tickers to DB (fetches from Finviz + caches result)
export const saveFinvizTickers = async (filters: FinvizFilterValues): Promise<FinvizScreenerResult & { cached?: boolean }> => {
  try {
    const response = await makeRequest<FinvizScreenerResult & { cached?: boolean }>(
      'POST',
      '/api/finviz_tickers',
      { filters },
      {
        timeout: 300000, // 5 minutes for large screener queries
        retries: 1,
        skipCache: true,
      }
    );
    return response;
  } catch (error: any) {
    let errorMessage = 'Failed to save Finviz tickers';
    if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      errorMessage = 'Cannot connect to server. Please check if the backend is running.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    return { success: false, tickers: [], count: 0, error: errorMessage };
  }
};

// Get cached Finviz tickers from DB
export interface CachedFinvizTickersResponse {
  success: boolean;
  tickers: string[];
  count: number;
  cached_at?: string;
  filters?: Record<string, any>;
  error?: string;
}

export const getCachedFinvizTickers = async (): Promise<CachedFinvizTickersResponse> => {
  try {
    return await makeRequest<CachedFinvizTickersResponse>('GET', '/api/finviz_tickers', undefined, {
      timeout: 10000,
      retries: 1,
    });
  } catch (error: any) {
    return { success: false, tickers: [], count: 0, error: error?.message };
  }
};

// Clear cached Finviz tickers from DB
export const clearCachedFinvizTickers = async (): Promise<{ success: boolean; message?: string }> => {
  try {
    return await makeRequest<{ success: boolean; message?: string }>('DELETE', '/api/finviz_tickers', undefined, {
      timeout: 10000,
      retries: 0,
    });
  } catch (error: any) {
    return { success: false, message: error?.message };
  }
};

export const fetchFinvizFilterReference = async (): Promise<FinvizFilterReferenceResponse> => {
  try {
    const response = await makeRequest<FinvizFilterReferenceResponse>(
      'GET',
      '/finviz_filter_reference',
      undefined,
      {
        timeout: 30000,
        retries: 1,
        skipCache: true,
      }
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      filters: {},
      error: error?.message || 'Failed to fetch Finviz filter reference',
    };
  }
};

// ---------------------------------------------------------------------------
// Deep Analysis API (uses fetch directly - not Axios queue)
// ---------------------------------------------------------------------------

/**
 * `fetch` wrapper that attaches the Firebase Bearer token the same way the
 * axios request interceptor (this file, ~api.ts:222-254) does - via
 * `authService.getIdToken()`. These deep-analysis routes are now
 * `@auth_required`-gated server-side; raw `fetch` bypasses the interceptor and
 * would 401 once enforcement flips on.
 *
 * Non-blocking by design: if no token is available (signed-out / Firebase
 * unavailable) the request goes out WITHOUT an Authorization header, exactly
 * as before - token retrieval never throws (getIdToken swallows internally)
 * and never gates the request. Existing headers (e.g. Content-Type), method,
 * body, AbortSignal, and all other RequestInit fields are preserved untouched;
 * we only merge in the Authorization header.
 */
export async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers || {});
  try {
    if (authService.isAvailable()) {
      const idToken = await authService.getIdToken();
      if (idToken) {
        headers.set('Authorization', `Bearer ${idToken}`);
      }
    }
  } catch {
    // Non-blocking: send unauthenticated rather than fail the request.
  }
  return fetch(url, { ...init, headers });
}

export async function fetchDeepHistorical(
  symbol: string,
  timeframeMode: 'DAILY' | 'WEEKLY' = 'DAILY',
  signal?: AbortSignal
): Promise<HistoricalBar[]> {
  const timeframe = timeframeMode === 'WEEKLY' ? 'WEEKLY' : 'DAILY';
  const resp = await authedFetch(
    `/api/deep-analysis/historical?symbol=${encodeURIComponent(symbol)}&timeframe=${encodeURIComponent(timeframe)}`,
    { signal: signal ?? null }
  );
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || 'Failed to fetch historical data');
  }
  return resp.json();
}

export async function fetchDeepFundamentals(symbol: string, signal?: AbortSignal): Promise<Fundamentals> {
  const resp = await authedFetch(`/api/deep-analysis/fundamentals?symbol=${encodeURIComponent(symbol)}`, { signal: signal ?? null });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || 'Failed to fetch fundamentals');
  }
  return resp.json();
}

/**
 * Spec #4 - fetch the backend-canonical entry signal (ATR-aware stop loss).
 * Returns null on failure so callers can gracefully fall back to entryEngine
 * local computation. The route is `@auth_required`-gated.
 */
export async function fetchEntrySignal(
  symbol: string,
  signal?: AbortSignal,
): Promise<{
  stopLoss: number;
  stopLossMethod?: 'ma' | 'atr' | 'swing' | 'flat' | 'capped';
  stopLossAnchorMA?: number | null;
  stopLossAtrPct?: number | null;
  stopLossDistancePct?: number | null;
} | null> {
  try {
    const resp = await authedFetch('/api/deep-analysis/entry-signal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbol }),
      signal: signal ?? null,
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    // 2026-05-09: backend ships snake_case (`stop_price`, `stop_method`,
    // `stop_anchor_ma`, `stop_distance_pct`, `atr_pct`). Map to the
    // camelCase contract the entry engine expects. Tolerates legacy
    // camelCase response shape too.
    const stopLossRaw = data?.stop_price ?? data?.stopLoss;
    if (stopLossRaw == null) return null;
    const methodRaw = (data?.stop_method ?? data?.stopLossMethod) as
      | 'ma' | 'atr' | 'swing' | 'flat' | 'capped' | undefined;
    const anchorRaw = data?.stop_anchor_ma ?? data?.stopLossAnchorMA ?? null;
    const atrPctRaw = data?.atr_pct ?? data?.stopLossAtrPct ?? null;
    const distancePctRaw = data?.stop_distance_pct ?? data?.stopLossDistancePct ?? null;
    const out: {
      stopLoss: number;
      stopLossMethod?: 'ma' | 'atr' | 'swing' | 'flat' | 'capped';
      stopLossAnchorMA?: number | null;
      stopLossAtrPct?: number | null;
      stopLossDistancePct?: number | null;
    } = {
      stopLoss: Number(stopLossRaw),
      stopLossAnchorMA: anchorRaw,
      stopLossAtrPct: atrPctRaw,
      stopLossDistancePct: distancePctRaw,
    };
    if (methodRaw !== undefined) {
      out.stopLossMethod = methodRaw;
    }
    return out;
  } catch {
    return null;
  }
}

export async function fetchSonarContext(
  ticker: string,
  asOfDate: string,
  signal?: AbortSignal,
  forceRefresh: boolean = false,
  timeframeMode: 'DAILY' | 'WEEKLY' = 'DAILY',
): Promise<any> {
  const timeframe = timeframeMode === 'WEEKLY' ? 'WEEKLY' : 'DAILY';
  const resp = await authedFetch('/api/deep-analysis/sonar-context', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticker, as_of_date: asOfDate, force_refresh: forceRefresh, timeframe }),
    signal: signal ?? null,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || `Sonar context error: ${resp.status}`);
  }
  return resp.json();
}

/**
 * TV Sidecar: fetch current companion status for a ticker.
 * Returns null-shape `{ tv_context: null, stale: true, age_seconds: 0 }` when
 * sidecar is off, disabled, or has no data - never throws on 2xx.
 */
export async function fetchTvSidecarStatus(
  ticker: string,
  signal?: AbortSignal,
): Promise<{
  tv_context: any | null;
  stale: boolean;
  age_seconds: number;
  captured_at?: string;
  source_version?: string;
  timeframe?: string;
  disabled?: boolean;
  error?: boolean;
  error_code?: 'unauthorized' | 'forbidden' | 'unavailable';
}> {
  const resp = await fetch(
    `/api/tv-sidecar/context?ticker=${encodeURIComponent(ticker)}`,
    { signal: signal ?? null, credentials: 'same-origin' },
  );
  if (!resp.ok) {
    // Non-2xx degrades to "off/unavailable" UI states without throwing.
    if (resp.status === 401) {
      return { tv_context: null, stale: true, age_seconds: 0, error: true, error_code: 'unauthorized' };
    }
    if (resp.status === 403) {
      return {
        tv_context: null,
        stale: true,
        age_seconds: 0,
        disabled: true,
        error: true,
        error_code: 'forbidden',
      };
    }
    return { tv_context: null, stale: true, age_seconds: 0, error: true, error_code: 'unavailable' };
  }
  return resp.json();
}

/**
 * TV Sidecar: rotate the user's companion bearer token. Plaintext token is
 * returned ONCE - caller must display and discard (no persistence in app state).
 */
export async function rotateTvSidecarToken(
  signal?: AbortSignal,
): Promise<{ token: string; warning: string }> {
  const resp = await fetch('/api/tv-sidecar/token/rotate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || `Rotate failed: ${resp.status}`);
  }
  return resp.json();
}

export async function streamSonarAnalysis(
  prompt: string,
  onChunk: (accumulated: string) => void,
  signal?: AbortSignal,
  cacheMeta?: { ticker: string; as_of_date: string; language: string; prompt_version: number; input_hash?: string },
  structured?: { mode: boolean; context: any },
): Promise<void> {
  const body: Record<string, unknown> = { prompt };
  if (cacheMeta) {
    body.ticker = cacheMeta.ticker;
    body.as_of_date = cacheMeta.as_of_date;
    body.language = cacheMeta.language;
    body.prompt_version = cacheMeta.prompt_version;
    if (cacheMeta.input_hash) body.input_hash = cacheMeta.input_hash;
  }
  if (structured?.mode) {
    body.structured_mode = true;
    body.structured_context = structured.context;
  }

  const resp = await authedFetch('/api/deep-analysis/sonar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: signal ?? null,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || `Sonar error: ${resp.status}`);
  }
  if (!resp.body) throw new Error('Response body is null - streaming not supported');
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulated = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop()!;
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const parsed = JSON.parse(line);
        if (parsed.done) return;
        if (parsed.error) throw new Error(parsed.error);
        accumulated += parsed.response;
        onChunk(accumulated);
      } catch (e: any) {
        if (e.message && !e.message.includes('JSON')) throw e;
      }
    }
  }
}

export interface SonarChatRequest {
  ticker: string;
  as_of_date: string;
  language: string;
  message: string;
  previous_user_questions?: string[];
}

export class SonarChatError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message?: string) {
    super(message ?? code);
    this.code = code;
    this.status = status;
  }
}

/**
 * Stream a follow-up chat turn for the /sonar-chat endpoint.
 *
 * Mirrors `streamSonarAnalysis`: NDJSON body, per-chunk accumulation,
 * surfaces 429 (quota or per-IP rate-limit) as `SonarChatError`.
 */
export async function streamSonarChat(
  req: SonarChatRequest,
  onChunk: (accumulated: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const resp = await authedFetch('/api/deep-analysis/sonar-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
    signal: signal ?? null,
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({ error: resp.statusText }));
    const code = data?.error || `http_${resp.status}`;
    throw new SonarChatError(code, resp.status, code);
  }
  if (!resp.body) throw new SonarChatError('no_stream', 500, 'Response body is null');

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulated = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop()!;
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const parsed = JSON.parse(line);
        if (parsed.error) throw new SonarChatError('stream_error', 500, parsed.error);
        if (parsed.response) {
          accumulated += parsed.response;
          onChunk(accumulated);
        }
        if (parsed.done) return accumulated;
      } catch (e: any) {
        if (e instanceof SonarChatError) throw e;
        if (e?.message && !e.message.includes('JSON')) throw e;
      }
    }
  }
  return accumulated;
}

// ============================================================================
// AI Daily Limits - Admin + User Usage API
// ============================================================================

export interface AiLimits {
  perplexity: number;
  grok: number;
}

export interface AiProviderUsage {
  used: number;
  limit: number;
  remaining: number;
}

export interface AiUsageSummary {
  date_utc: string;
  resets_at_utc: string;
  providers: {
    perplexity: AiProviderUsage;
    grok: AiProviderUsage;
  };
}

export const getAdminAiLimits = async (): Promise<{ success: boolean; limits: AiLimits }> => {
  const response = await api.get('/api/admin/ai-limits');
  return response.data;
};

export const updateAdminAiLimits = async (payload: Partial<AiLimits>): Promise<{ success: boolean; limits: AiLimits }> => {
  const response = await api.put('/api/admin/ai-limits', payload);
  return response.data;
};

export const getAiUsage = async (): Promise<AiUsageSummary> => {
  const response = await api.get('/api/ai-usage');
  return response.data;
};

// ============================================================================
// Auto-Screen v1 - Per-user hourly background screening
// Backend contract (Phase E shipped):
//   GET  /api/auto-screen/feature-status  → { enabled: boolean }   (no auth)
//   GET  /api/auto-screen/settings        → AutoScreenSettings
//   PUT  /api/auto-screen/settings        → AutoScreenSettings  (partial update)
//                                            402 paid_tier_required, 503 feature_disabled
//   POST /api/auto-screen/run-now         → AutoScreenRunNowResponse
//                                            409 schedule_not_configured
//   GET  /api/auto-screen/runs?limit=N    → AutoScreenRun[]
// ============================================================================

export interface AutoScreenSettings {
  enabled: boolean;
  intervalMinutes: number;          // server-locked at 60 in v1; UI displays static text
  marketHoursOnly: boolean;
  requireUserActive: boolean;
  notifyEmail: boolean;
  notifyTelegram: boolean;
  notifyWhatsapp: boolean;
  onlyNotifyOnNew: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;     // 'queued'|'running'|'completed'|'failed'|'canceled'|'skipped'|null
  nextEligibleAt: string | null;
  runNowRequestedAt: string | null;
  consecutiveFailures: number;
}

export type AutoScreenSettingsPatch = Partial<
  Pick<
    AutoScreenSettings,
    | 'enabled'
    | 'marketHoursOnly'
    | 'requireUserActive'
    | 'notifyEmail'
    | 'notifyTelegram'
    | 'notifyWhatsapp'
    | 'onlyNotifyOnNew'
  >
>;

export interface AutoScreenRunNowResponse {
  queued: boolean;
  alreadyPending: boolean;
  estimatedPickupSeconds: number;
}

export interface AutoScreenRun {
  runId: string;
  runKind: string;          // 'manual' | 'auto' | 'run_now'
  status: string;           // 'queued'|'running'|'completed'|'failed'|'canceled'|'skipped'
  skipReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  matchedCount: number | null;
  newTickerCount: number | null;
  metadataNotified: boolean | null;
}

export interface AutoScreenFeatureStatus {
  enabled: boolean;
}

export const getAutoScreenFeatureStatus = async (): Promise<AutoScreenFeatureStatus> => {
  const response = await api.get('/api/auto-screen/feature-status');
  return response.data;
};

export const getAutoScreenSettings = async (): Promise<AutoScreenSettings> => {
  const response = await api.get('/api/auto-screen/settings');
  return response.data;
};

export const updateAutoScreenSettings = async (
  patch: AutoScreenSettingsPatch
): Promise<AutoScreenSettings> => {
  const response = await api.put('/api/auto-screen/settings', patch);
  return response.data;
};

export const triggerAutoScreenRunNow = async (): Promise<AutoScreenRunNowResponse> => {
  const response = await api.post('/api/auto-screen/run-now');
  return response.data;
};

export const getAutoScreenRuns = async (limit: number = 20): Promise<AutoScreenRun[]> => {
  const response = await api.get('/api/auto-screen/runs', { params: { limit } });
  // Backend returns array directly per Phase E contract; tolerate { runs: [...] } wrapper just in case.
  if (Array.isArray(response.data)) return response.data;
  if (response.data && Array.isArray(response.data.runs)) return response.data.runs;
  return [];
};


// ============================================
// Admin runtime-config (PR-B3 - frontend half of spec §5)
// ============================================
// Type imports for the admin runtime-config block live at the top of the
// file (per the import/first ESLint rule). This comment marks the block.

export interface RuntimeConfigListResponse {
  items: EffectiveConfig[];
  tier_4_denylist: string[];
}

export interface RuntimeConfigAuditResponse {
  items: AuditEntry[];
  limit: number;
  offset: number;
}

const RTC_BASE = '/api/admin/runtime-config';

// 409 conflicts surface a fresh EffectiveConfig from the server. We re-throw
// a typed error so the slice can roll back optimistic edits + reseat the row.
export class RuntimeConfigConflict extends Error {
  current: EffectiveConfig | null;
  constructor(message: string, current: EffectiveConfig | null) {
    super(message);
    this.name = 'RuntimeConfigConflict';
    this.current = current;
  }
}

export const listRuntimeConfig = async (): Promise<RuntimeConfigListResponse> => {
  const response = await api.get(RTC_BASE);
  return response.data;
};

export const getRuntimeConfig = async (
  key: string,
): Promise<{ flag: EffectiveConfig; audit: AuditEntry[] }> => {
  const response = await api.get(`${RTC_BASE}/${encodeURIComponent(key)}`);
  return response.data;
};

export const setRuntimeConfig = async (
  key: string,
  value: string,
  expectedVersion: number | null,
  reason: string | null,
): Promise<EffectiveConfig> => {
  try {
    const headers: Record<string, string> = {};
    if (expectedVersion !== null) {
      headers['If-Match'] = String(expectedVersion);
    }
    const response = await api.put(
      `${RTC_BASE}/${encodeURIComponent(key)}`,
      {
        value,
        expected_version: expectedVersion,
        reason: reason ?? '',
      },
      { headers },
    );
    return response.data.flag as EffectiveConfig;
  } catch (err: any) {
    if (err?.response?.status === 409) {
      const body = err.response?.data;
      const current = (body?.value !== undefined ? body : null) as EffectiveConfig | null;
      throw new RuntimeConfigConflict(
        body?.message || 'runtime_config version mismatch',
        current,
      );
    }
    throw err;
  }
};

export const revertRuntimeConfig = async (key: string): Promise<{ deleted: boolean }> => {
  const response = await api.delete(`${RTC_BASE}/${encodeURIComponent(key)}`);
  return response.data;
};

export const getRuntimeConfigAudit = async (
  limit: number = 50,
  offset: number = 0,
): Promise<RuntimeConfigAuditResponse> => {
  const response = await api.get(`${RTC_BASE}/audit`, { params: { limit, offset } });
  return response.data;
};

// ============================================
// Position Diary API (F1 - shared infra)
// ============================================

const POSITIONS_BASE = '/api/positions';

// Cache key prefix for positions list - invalidated on mutations.
const POS_LIST_CACHE_PREFIX = 'GET:/api/positions:';

/** Invalidate the positions list cache after any mutation. */
const invalidatePositionsListCache = () => {
  for (const key of Array.from(requestCache.keys())) {
    if (key.startsWith(POS_LIST_CACHE_PREFIX)) {
      requestCache.delete(key);
    }
  }
};

// ---------------------------------------------------------------------------
// Feature-status probe (no auth required - mirrors getAutoScreenFeatureStatus)
// ---------------------------------------------------------------------------

export const getPositionDiaryFeatureStatus = async (): Promise<PositionFeatureStatus> => {
  const response = await api.get(`${POSITIONS_BASE}/feature-status`);
  return response.data;
};

// ---------------------------------------------------------------------------
// Stats (aggregate)
// ---------------------------------------------------------------------------

export const getPositionStats = async (): Promise<PositionStatsResponse> => {
  const cacheKey = createCacheKey('GET', `${POSITIONS_BASE}/stats/summary`);
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const response = await makeRequest<PositionStatsResponse>(
    'GET',
    `${POSITIONS_BASE}/stats/summary`,
    undefined,
    { cacheTTL: 60000 }
  );
  setCachedData(cacheKey, response, 60000);
  return response;
};

// ---------------------------------------------------------------------------
// Collection - list + create
// ---------------------------------------------------------------------------

export const getPositions = async (
  filters?: PositionListFilters
): Promise<PositionsListResponse> => {
  const params: Record<string, unknown> = { ...(filters ?? {}) };
  // Build query string so list filters reach the backend (makeRequest only
  // accepts a body, not query params - append them directly to the URL).
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) =>
      Array.isArray(v)
        ? v.map(item => `${encodeURIComponent(k)}=${encodeURIComponent(String(item))}`).join('&')
        : `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
    )
    .join('&');
  const url = qs ? `${POSITIONS_BASE}?${qs}` : POSITIONS_BASE;
  const cacheKey = createCacheKey('GET', POSITIONS_BASE, params);
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const response = await makeRequest<PositionsListResponse>(
    'GET',
    url,
    undefined,
    { cacheTTL: 30000 }
  );
  setCachedData(cacheKey, response, 30000);
  return response;
};

export const createPosition = async (
  payload: CreatePositionPayload
): Promise<PositionDetailResponse> => {
  const response = await makeRequest<PositionDetailResponse>(
    'POST',
    POSITIONS_BASE,
    {
      ...payload,
      ticker: payload.ticker.trim().toUpperCase(),
    }
  );
  invalidatePositionsListCache();
  return response;
};

// ---------------------------------------------------------------------------
// Single position - read / update / close / archive / delete
// ---------------------------------------------------------------------------

export const getPosition = async (id: number): Promise<PositionDetailResponse> => {
  const cacheKey = createCacheKey('GET', `${POSITIONS_BASE}/${id}`);
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const response = await makeRequest<PositionDetailResponse>(
    'GET',
    `${POSITIONS_BASE}/${id}`,
    undefined,
    { cacheTTL: 15000 }
  );
  setCachedData(cacheKey, response, 15000);
  return response;
};

export const updatePosition = async (
  id: number,
  payload: UpdatePositionPayload
): Promise<PositionDetailResponse> => {
  const response = await makeRequest<PositionDetailResponse>(
    'PUT',
    `${POSITIONS_BASE}/${id}`,
    payload
  );
  invalidatePositionsListCache();
  requestCache.delete(createCacheKey('GET', `${POSITIONS_BASE}/${id}`));
  return response;
};

export const closePosition = async (
  id: number,
  payload: ClosePositionPayload
): Promise<PositionDetailResponse> => {
  const response = await makeRequest<PositionDetailResponse>(
    'POST',
    `${POSITIONS_BASE}/${id}/close`,
    payload
  );
  invalidatePositionsListCache();
  requestCache.delete(createCacheKey('GET', `${POSITIONS_BASE}/${id}`));
  return response;
};

export const archivePosition = async (id: number): Promise<{ success: boolean }> => {
  const response = await makeRequest<{ success: boolean }>(
    'POST',
    `${POSITIONS_BASE}/${id}/archive`
  );
  invalidatePositionsListCache();
  requestCache.delete(createCacheKey('GET', `${POSITIONS_BASE}/${id}`));
  return response;
};

/**
 * Hard-delete a position and all child rows (irreversible).
 * Caller must obtain typed confirmation before invoking.
 */
export const deletePosition = async (
  id: number,
  confirmTicker?: string
): Promise<{ success: boolean }> => {
  const response = await makeRequest<{ success: boolean }>(
    'DELETE',
    `${POSITIONS_BASE}/${id}`,
    confirmTicker ? { confirm_ticker: confirmTicker.toUpperCase() } : undefined
  );
  invalidatePositionsListCache();
  requestCache.delete(createCacheKey('GET', `${POSITIONS_BASE}/${id}`));
  return response;
};

// ---------------------------------------------------------------------------
// Levels update
// ---------------------------------------------------------------------------

export const updatePositionLevels = async (
  id: number,
  payload: UpdatePositionLevelsPayload
): Promise<{ success: boolean; levels: unknown }> => {
  const response = await makeRequest<{ success: boolean; levels: unknown }>(
    'POST',
    `${POSITIONS_BASE}/${id}/levels`,
    payload
  );
  requestCache.delete(createCacheKey('GET', `${POSITIONS_BASE}/${id}`));
  return response;
};

// ---------------------------------------------------------------------------
// Chart overlay (B4)
// ---------------------------------------------------------------------------

export const getPositionChartOverlay = async (
  id: number
): Promise<Record<string, unknown>> => {
  const cacheKey = createCacheKey('GET', `${POSITIONS_BASE}/${id}/chart-overlay`);
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const response = await makeRequest<Record<string, unknown>>(
    'GET',
    `${POSITIONS_BASE}/${id}/chart-overlay`,
    undefined,
    { cacheTTL: 30000 }
  );
  setCachedData(cacheKey, response, 30000);
  return response;
};

// ---------------------------------------------------------------------------
// Timeline (decision ledger - read model)
// ---------------------------------------------------------------------------

export const getPositionTimeline = async (
  id: number
): Promise<PositionTimelineResponse> => {
  const response = await makeRequest<PositionTimelineResponse>(
    'GET',
    `${POSITIONS_BASE}/${id}/timeline`,
    undefined,
    { skipCache: true }
  );
  return response;
};

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export const getPositionAlerts = async (
  id: number
): Promise<PositionAlertsResponse> => {
  const response = await makeRequest<PositionAlertsResponse>(
    'GET',
    `${POSITIONS_BASE}/${id}/alerts`,
    undefined,
    { skipCache: true }
  );
  return response;
};

export const ackPositionAlert = async (
  positionId: number,
  alertId: number
): Promise<{ success: boolean }> => {
  return makeRequest<{ success: boolean }>(
    'POST',
    `${POSITIONS_BASE}/${positionId}/alerts/${alertId}/ack`
  );
};

// ---------------------------------------------------------------------------
// Snapshot
// ---------------------------------------------------------------------------

// getPositionEntrySnapshot was a duplicate of getPositionChartOverlay (same
// URL, conflicting return type).  Alias kept for call-site compatibility; both
// refer to the chart-overlay endpoint.
export const getPositionEntrySnapshot = getPositionChartOverlay;

// ---------------------------------------------------------------------------
// Daily summary (B7)
// ---------------------------------------------------------------------------

export const getPositionDailySummary = async (
  id: number
): Promise<PositionDailySummaryResponse> => {
  const response = await makeRequest<PositionDailySummaryResponse>(
    'GET',
    `${POSITIONS_BASE}/${id}/daily-summary`,
    undefined,
    { cacheTTL: 60000 }
  );
  return response;
};
